/* eslint-disable @typescript-eslint/no-explicit-any */
import * as XLSX from 'xlsx';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  Table,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { tuitionDetailActions, tuitionInvoiceActions, studentActions, classActions, attendanceActions, attendanceStudentRecordActions, scheduleActions, userActions, notificationActions } from '../../redux/actions';
import tuitionInvoiceService from '../../services/tuitionInvoice.service';
import tuitionDetailService from '../../services/tuitionDetail.service';
import classMonthlySessionService from '../../services/classMonthlySession.service';
import { studentService } from '../../services/student.service';
import { attendanceStudentRecordService } from '../../services/attendanceStudentRecord.service';
import { classService } from '../../services/class.service';
import { attendanceSessionService } from '../../services/attendanceSession.service';
import styled, { keyframes } from 'styled-components';
import bankAccountService, { BankAccount } from '../../services/bankAccount.service';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  animation: ${fadeIn} 0.45s ease-out;
  background: #f4f6fb;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, #1a237e 0%, #283593 40%, #1565c0 100%);
  padding: 22px 28px 20px;
  border-radius: 0 0 20px 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(21,101,192,0.18);
`;

const PageTitle = styled.h2`
  color: #fff;
  font-weight: 800;
  font-size: 1.45rem;
  margin: 0 0 16px;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 4px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.22);
  border-radius: 10px;
  padding: 5px 12px;
  backdrop-filter: blur(4px);
`;

const FilterLabel = styled.span`
  color: rgba(255,255,255,0.8);
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StyledSelect = styled.select`
  background: rgba(255,255,255,0.15);
  border: none;
  border-radius: 7px;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  padding: 3px 8px;
  cursor: pointer;
  outline: none;
  -webkit-appearance: none;
  min-width: 80px;
  &:focus { box-shadow: 0 0 0 2px rgba(255,255,255,0.35); }
  option { background: #1a237e; color: #fff; }
`;

const ActionBtn = styled.button<{ variant?: 'primary' | 'success' | 'outline' }>`
  padding: 7px 16px;
  border-radius: 9px;
  border: none;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.18s;
  white-space: nowrap;
  background: ${props => props.variant === 'success' ? 'linear-gradient(135deg,#43a047,#2e7d32)' :
    props.variant === 'outline' ? 'rgba(255,255,255,0.13)' :
      'linear-gradient(135deg,#1976d2,#1565c0)'
  };
  color: #fff;
  border: ${props => props.variant === 'outline' ? '1.5px solid rgba(255,255,255,0.4)' : 'none'};
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(0,0,0,0.22);
  }
  &:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
`;

const ContentCard = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  padding: 22px 24px;
  margin-bottom: 22px;
`;

const SectionTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e8eaf6;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 14px;
  margin-bottom: 24px;
`;

const KpiCard = styled.div<{ accent?: string }>`
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.07);
  border-left: 4px solid ${props => props.accent || '#1976d2'};
  transition: transform 0.18s, box-shadow 0.18s;
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.1); }
`;

const KpiLabel = styled.div`
  font-size: 0.72rem;
  color: #90a4ae;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
`;

const KpiValue = styled.div<{ color?: string }>`
  font-size: 1.35rem;
  font-weight: 800;
  color: ${props => props.color || '#1565c0'};
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const SearchInput = styled.input`
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  padding: 7px 14px;
  font-size: 0.88rem;
  outline: none;
  background: #f8f9ff;
  width: 100%;
  transition: border 0.18s, box-shadow 0.18s;
  &:focus {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25,118,210,0.12);
    background: #fff;
  }
`;

const NativeSelect = styled.select`
  border: 1.5px solid #e0e0e0;
  border-radius: 10px;
  padding: 7px 12px;
  font-size: 0.88rem;
  background: #f8f9ff;
  color: #333;
  outline: none;
  width: 100%;
  cursor: pointer;
  transition: border 0.18s, box-shadow 0.18s;
  &:focus {
    border-color: #1976d2;
    box-shadow: 0 0 0 3px rgba(25,118,210,0.12);
    background: #fff;
  }
`;

// Legacy aliases kept for compatibility
const StatsCard = styled.div``;
const StatsValue = styled.div``;
const StatsLabel = styled.div``;


const TuitionStatistics: React.FC = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState('1');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Calculate previous month for display
  const previousMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
  const previousMonthYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [selectedTuitionDetails, setSelectedTuitionDetails] = useState<any[]>([]); // Changed to array
  const [selectedStudentsForInvoice, setSelectedStudentsForInvoice] = useState<string[]>([]); // For multi-select
  const [transferImage, setTransferImage] = useState<File | null>(null);
  const [transferImagePreview, setTransferImagePreview] = useState<string>('');
  const [searchName, setSearchName] = useState(''); // For search filter
  const [filterTeacherId, setFilterTeacherId] = useState(''); // Filter by teacher
  const [filterBankAccountId, setFilterBankAccountId] = useState(''); // Filter by bank account
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [invoiceDetailModal, setInvoiceDetailModal] = useState(false);
  const [selectedInvoiceForView, setSelectedInvoiceForView] = useState<any>(null);
  const [studentInvoices, setStudentInvoices] = useState<Record<number, any>>({}); // Map studentId -> invoice
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0); // Bump to force invoice reload
  const [siblingConfirmTarget, setSiblingConfirmTarget] = useState<any>(null); // detail waiting for sibling choice
  const [hasCopiedConfirm, setHasCopiedConfirm] = useState(false); // Must copy cash confirmation before saving
  const [siblingsMap, setSiblingsMap] = useState<Record<number, number[]>>({}); // Map studentId -> sibling IDs
  // unpaidMonthsMap: studentId -> list of unpaid months (excl. current month if already shown)
  const [unpaidMonthsMap, setUnpaidMonthsMap] = useState<Record<number, { tuitionDetailId: number; month: number; year: number; totalFee: number }[]>>({});
  // extraMonthsSelected: studentId -> list of tuitionDetailIds for extra months chosen by user
  const [extraMonthsSelected, setExtraMonthsSelected] = useState<Record<number, number[]>>({});
  const [loadingUnpaidMonths, setLoadingUnpaidMonths] = useState(false);
  const [isAutoCalculating, setIsAutoCalculating] = useState(false);
  // Modal Tự động tính HP
  const [autoCalcModal, setAutoCalcModal] = useState(false);
  const [autoCalcLoading, setAutoCalcLoading] = useState(false);
  const [autoCalcFilter, setAutoCalcFilter] = useState('');
  const [autoCalcStudents, setAutoCalcStudents] = useState<{ student: any; hasInvoice: boolean; checked: boolean }[]>([]);
  const [isEditInvoiceMode, setIsEditInvoiceMode] = useState(false); // true = đang sửa HĐ cũ
  const [currentEditInvoiceId, setCurrentEditInvoiceId] = useState<number | null>(null); // ID hoá đơn đang sửa
  // Ngày nghỉ hẳn giữa tháng — để tính lại HP trước khi tạo HĐ
  const [quitDateForRecalc, setQuitDateForRecalc] = useState<string>('');
  const [isRecalculating, setIsRecalculating] = useState(false);
  // Kết quả tính lại HP — hiển thị chi tiết ngày đi/nghỉ trong modal
  const [recalcResult, setRecalcResult] = useState<{
    attendedDates: string[];
    absentDates: string[];
    attendedCount: number;
    absentCountThisMonth: number;
    absencesLastMonth: number;
    newTotalFee: number;
    feePerSession: number;
  } | null>(null);
  const [showRevenue, setShowRevenue] = useState(false); // For hiding/showing revenue
  const [showPaid, setShowPaid] = useState(false); // For hiding/showing paid amount
  const [showUnpaid, setShowUnpaid] = useState(false); // For hiding/showing unpaid amount
  const [payTab2Search, setPayTab2Search] = useState(''); // Search in payment status tab
  const [currentPage, setCurrentPage] = useState(1); // For pagination
  const [itemsPerPage] = useState(30); // Items per page
  const [invoiceFormData, setInvoiceFormData] = useState({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '', // no default — user must pick
    payerName: '',
    notes: '',
  });

  // ——— Sửa / Xóa học phí ———
  const [editDetailModal, setEditDetailModal] = useState(false);
  const [editingDetail, setEditingDetail] = useState<any>(null);
  const [isSavingDetail, setIsSavingDetail] = useState(false);
  const [editDetailForm, setEditDetailForm] = useState({
    sessionsThisMonth: 0,
    feePerSession: 0,
    currentMonthFee: 0,
    absencesLastMonth: 0,
    lastMonthAbsenceFee: 0,
    hasExtraFee: false,
    extraFeeAmount: 0,
    extraFeeLabel: '',
    totalFee: 0,
    note: '',
  });

  const tuitionDetails = useAppSelector((state: any) => state.tuitionDetails.items);
  const invoiceStatistics = useAppSelector((state: any) => state.tuitionInvoices.statistics);
  const loading = useAppSelector(
    (state: any) => state.tuitionDetails.loading || state.tuitionInvoices.loading
  );
  const students = useAppSelector((state: any) => state.students.studentsList);
  const classes = useAppSelector((state: any) => state.classes.classesList);
  const teachers = useAppSelector((state: any) => state.users.items);
  const schedulesList = useAppSelector((state: any) => state.schedules.schedulesList);
  const attendanceSessions = useAppSelector((state: any) => state.attendanceSessions.attendanceSessionsList);
  const previousMonthAttendanceStudentRecords = useAppSelector((state: any) => state.attendanceStudentRecords.attendanceStudentRecordsList);

  // Filter tuition details by search name, teacher, and bank account
  const filteredTuitionDetails = tuitionDetails.filter((detail: any) => {
    if (searchName.trim()) {
      const studentName = detail.student?.name?.toLowerCase() || '';
      if (!studentName.includes(searchName.toLowerCase())) return false;
    }
    if (filterTeacherId) {
      const cls = classes.find((c: any) => c.id === detail.classId);
      if (!cls || String(cls.teacherId) !== filterTeacherId) return false;
    }
    if (filterBankAccountId) {
      // Try Redux (active students) first, then fall back to detail.student (includes bankAccountId for archived)
      const student = students.find((s: any) => s.id === detail.studentId)
        || detail.student;
      if (!student || String(student.bankAccountId) !== filterBankAccountId) return false;
    }
    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredTuitionDetails.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTuitionDetails.slice(indexOfFirstItem, indexOfLastItem);
  // Stable key: changes when actual items change (not just count), used as effect dependency
  const currentItemsKey = currentItems.map((d: any) => d.id).join(',');

  useEffect(() => {
    // Clear stale invoice cache whenever the month/year changes so the
    // action-column badges always reflect the selected month's invoices.
    setStudentInvoices({});
    setInvoiceRefreshKey(k => k + 1);
    loadData();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    // Reset to page 1 when search changes
    setCurrentPage(1);
  }, [searchName]);

  useEffect(() => {
    // Load invoices for current page items only
    const loadInvoicesForCurrentPage = async () => {
      if (currentItems.length === 0) return;

      // Guard: skip if currentItems still belongs to a different month (Redux not yet updated)
      const allMatchMonth = currentItems.every(
        (detail: any) => detail.month === selectedMonth && detail.year === selectedYear
      );
      if (!allMatchMonth) return;

      // Get tuition detail IDs that don't have invoices loaded yet
      const idsToLoad = currentItems
        .filter((detail: any) => !studentInvoices[detail.studentId])
        .map((detail: any) => detail.id);

      if (idsToLoad.length === 0) return; // All invoices already loaded

      try {
        // Call batch API with array of tuition detail IDs
        const result = await tuitionInvoiceService.getByTuitionDetailIds(idsToLoad);

        if (result.success && result.data) {
          const invoicesMap = { ...studentInvoices };

          // Map invoices by studentId (result.data is indexed by tuitionDetailId)
          currentItems.forEach((detail: any) => {
            // Explicitly mark students with NO invoice as null (to prevent re-fetching)
            invoicesMap[detail.studentId] = result.data[detail.id] ?? null;
          });

          setStudentInvoices(invoicesMap);
        }
      } catch (error) {
        console.error('Error loading invoices:', error);
      }
    };

    loadInvoicesForCurrentPage();
  }, [currentPage, currentItemsKey, invoiceRefreshKey, selectedMonth, selectedYear]);

  // Khi tick chọn học sinh (kể cả anh/em ruột được auto-tick),
  // load invoice cho những học sinh chưa có trong studentInvoices (có thể ở trang khác)
  useEffect(() => {
    if (selectedStudentsForInvoice.length === 0) return;

    const loadInvoicesForSelected = async () => {
      // Tìm những học sinh được chọn mà chưa có invoice trong state
      const detailsToLoad = tuitionDetails.filter((detail: any) =>
        selectedStudentsForInvoice.includes(String(detail.studentId)) &&
        !studentInvoices[detail.studentId]
      );
      if (detailsToLoad.length === 0) return;

      const idsToLoad = detailsToLoad.map((d: any) => d.id);
      try {
        const result = await tuitionInvoiceService.getByTuitionDetailIds(idsToLoad);
        if (result.success && result.data) {
          setStudentInvoices(prev => {
            const updated = { ...prev };
            detailsToLoad.forEach((detail: any) => {
              if (result.data[detail.id]) {
                updated[detail.studentId] = result.data[detail.id];
              } else {
                // Đánh dấu là đã check (null = không có HĐ), tránh fetch lại
                if (!(detail.studentId in updated)) {
                  updated[detail.studentId] = null;
                }
              }
            });
            return updated;
          });
        }
      } catch (error) {
        console.error('Error loading invoices for selected students:', error);
      }
    };

    loadInvoicesForSelected();
  }, [selectedStudentsForInvoice]);

  const loadData = async () => {
    dispatch(tuitionDetailActions.getAll({ month: selectedMonth, year: selectedYear }));
    dispatch(tuitionInvoiceActions.getStatistics(selectedMonth, selectedYear));

    // Load students, classes, schedules, teachers for filter and auto-calculate feature
    dispatch(studentActions.getAll());
    dispatch(classActions.getAll());
    dispatch(scheduleActions.getAll());
    dispatch(userActions.getAllTeachers());

    // Load bank accounts for filter
    bankAccountService.getAll().then(setBankAccounts).catch(() => setBankAccounts([]));
  };

  // Load siblings map whenever students list changes
  useEffect(() => {
    if (!students || students.length === 0) return;
    const ids = students.map((s: any) => s.id);
    studentService.getBatchSiblings(ids)
      .then((map: Record<number, any[]>) => {
        // Convert to map of id -> sibling id array
        const simplified: Record<number, number[]> = {};
        Object.entries(map).forEach(([sid, siblings]) => {
          simplified[Number(sid)] = (siblings as any[]).map((s: any) => s.id);
        });
        setSiblingsMap(simplified);
      })
      .catch(() => { /* silent */ });
  }, [students]);

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const toggleInvoiceModal = () => {
    if (invoiceModal) {
      // Reset when closing
      setSelectedTuitionDetails([]);
      setSelectedStudentsForInvoice([]);
      setTransferImage(null);
      setTransferImagePreview('');
      setIsEditInvoiceMode(false);
      setCurrentEditInvoiceId(null);
    }
    setHasCopiedConfirm(false);
    setQuitDateForRecalc(''); // reset quit date khi đóng modal
    setRecalcResult(null);    // reset recalc details
    setInvoiceModal(!invoiceModal);
  };

  const handleCreateInvoice = async (tuitionDetail: any) => {
    // Nếu học sinh có siblings và chưa có hoá đơn → hỏi trước
    const siblingIds = siblingsMap[tuitionDetail.studentId] || [];
    const unInvoicedSiblings = siblingIds.filter(sid => !studentInvoices[sid]);
    if (unInvoicedSiblings.length > 0) {
      setSiblingConfirmTarget(tuitionDetail);
      return;
    }
    await _openCreateInvoiceModal([tuitionDetail]);
  };

  // Open modal với danh sách detail được chọn
  const _openCreateInvoiceModal = async (details: any[]) => {
    const totalAmount = details.reduce((s, d) => s + parseFloat(d.totalFee || 0), 0);
    setSelectedTuitionDetails(details);
    setSelectedStudentsForInvoice(details.map(d => String(d.studentId)));
    setExtraMonthsSelected({});
    setInvoiceFormData({
      amount: totalAmount,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: '',
      payerName: '',
      notes: details.length > 1 ? `Thanh toán cho ${details.length} học sinh` : '',
    });
    setTransferImage(null);
    setTransferImagePreview('');
    const studentIds = details.map(d => d.studentId);
    setLoadingUnpaidMonths(true);
    try {
      const res = await tuitionDetailService.getUnpaidMonths(studentIds);
      const currentDetailIdByStudent: Record<number, number> = {};
      details.forEach(d => { currentDetailIdByStudent[d.studentId] = d.id; });
      const filtered: Record<number, any[]> = {};
      studentIds.forEach(sid => {
        const raw: any[] = res.data?.[sid] || [];
        filtered[sid] = raw.filter((m: any) => m.tuitionDetailId !== currentDetailIdByStudent[sid]);
      });
      setUnpaidMonthsMap(filtered);
    } catch { setUnpaidMonthsMap({}); }
    finally { setLoadingUnpaidMonths(false); }
    toggleInvoiceModal();
  };

  // Sửa hoá đơn: pre-fill payerName & paymentMethod từ hoá đơn cũ
  const handleEditInvoice = async (tuitionDetail: any) => {
    const existingInvoice = studentInvoices[tuitionDetail.studentId];
    setSelectedTuitionDetails([tuitionDetail]);
    setSelectedStudentsForInvoice([String(tuitionDetail.studentId)]);
    setExtraMonthsSelected({});
    // Set edit mode — sẽ gọi update thay vì create khi save
    setIsEditInvoiceMode(true);
    setCurrentEditInvoiceId(existingInvoice?.id || null);
    setInvoiceFormData({
      amount: existingInvoice?.amount ?? tuitionDetail.totalFee,
      paymentDate: existingInvoice?.paymentDate
        ? existingInvoice.paymentDate.split('T')[0]
        : new Date().toISOString().split('T')[0],
      paymentMethod: existingInvoice?.paymentMethod || '',
      payerName: existingInvoice?.payerName || '',
      notes: existingInvoice?.notes || '',
    });
    // Pre-fill transfer image preview if existed
    if (existingInvoice?.transferImageUrl) {
      setTransferImagePreview(existingInvoice.transferImageUrl);
    } else {
      setTransferImage(null);
      setTransferImagePreview('');
    }
    setLoadingUnpaidMonths(true);
    try {
      const res = await tuitionDetailService.getUnpaidMonths([tuitionDetail.studentId]);
      const rawList: any[] = (res.data?.[tuitionDetail.studentId] || []);
      const filtered = rawList.filter((m: any) => m.tuitionDetailId !== tuitionDetail.id);
      setUnpaidMonthsMap({ [tuitionDetail.studentId]: filtered });
    } catch { setUnpaidMonthsMap({}); }
    finally { setLoadingUnpaidMonths(false); }
    toggleInvoiceModal();
  };

  const handleCreateMultipleInvoice = async () => {
    // Chỉ lấy học sinh CHƯA có hoá đơn (null = đã check không có HĐ, object = đã có HĐ)
    const selectedDetails = tuitionDetails.filter((detail: any) =>
      selectedStudentsForInvoice.includes(String(detail.studentId)) &&
      !studentInvoices[detail.studentId] // null hoặc undefined → chưa có HĐ
    );

    if (selectedDetails.length === 0) {
      dispatch(notificationActions.addNotification('Tất cả học sinh đã có hoá đơn rồi!', 'INFO'));
      return;
    }

    const totalAmount = selectedDetails.reduce((sum: number, detail: any) => {
      return sum + parseFloat(detail.totalFee || 0);
    }, 0);
    const studentNames = selectedDetails.map((d: any) => d.student?.name).join(', ');

    setSelectedTuitionDetails(selectedDetails);
    setExtraMonthsSelected({});
    setInvoiceFormData({
      amount: totalAmount,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: '',
      payerName: '',
      notes: `Thanh toán cho ${selectedDetails.length} học sinh`,
    });
    // Fetch unpaid months for all selected students
    const studentIds = selectedDetails.map((d: any) => d.studentId);
    setLoadingUnpaidMonths(true);
    try {
      const res = await tuitionDetailService.getUnpaidMonths(studentIds);
      // For each student, exclude their current-month detail from unpaid list
      const currentDetailIdByStudent: Record<number, number> = {};
      selectedDetails.forEach((d: any) => { currentDetailIdByStudent[d.studentId] = d.id; });
      const filtered: Record<number, any[]> = {};
      studentIds.forEach((sid: number) => {
        const raw: any[] = res.data?.[sid] || [];
        filtered[sid] = raw.filter((m: any) => m.tuitionDetailId !== currentDetailIdByStudent[sid]);
      });
      setUnpaidMonthsMap(filtered);
    } catch { setUnpaidMonthsMap({}); }
    finally { setLoadingUnpaidMonths(false); }
    toggleInvoiceModal();
  };

  const toggleStudentForInvoice = (studentId: string) => {
    const numId = Number(studentId);
    const siblingIds = (siblingsMap[numId] || []).map(String);

    setSelectedStudentsForInvoice((prev) => {
      if (prev.includes(studentId)) {
        // Bỏ chọn học sinh này (KHÔNG tự bỏ anh/em — để người dùng tự quyết)
        return prev.filter((id) => id !== studentId);
      } else {
        // Chọn học sinh này + auto-tick tất cả anh/em chưa được chọn
        const toAdd = [studentId, ...siblingIds].filter((id) => !prev.includes(id));
        return [...prev, ...toAdd];
      }
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        dispatch(notificationActions.addNotification('Kích thước ảnh không được vượt quá 10MB!', 'DANGER'));
        return;
      }
      if (!file.type.startsWith('image/')) {
        dispatch(notificationActions.addNotification('Vui lòng chọn file ảnh!', 'DANGER'));
        return;
      }
      setTransferImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setTransferImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        dispatch(notificationActions.addNotification('Kích thước ảnh không được vượt quá 10MB!', 'DANGER'));
        return;
      }
      if (!file.type.startsWith('image/')) {
        dispatch(notificationActions.addNotification('Vui lòng chọn file ảnh!', 'DANGER'));
        return;
      }
      setTransferImage(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setTransferImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleViewInvoice = async (student: any) => {
    // Check if we already have the invoice in memory
    const invoice = studentInvoices[student.studentId];
    if (invoice) {
      setSelectedInvoiceForView(invoice);
      setInvoiceDetailModal(true);
      return;
    }

    // Otherwise fetch from API
    try {
      const tuitionDetail = tuitionDetails.find((d: any) => d.studentId === student.studentId);
      if (tuitionDetail) {
        const result: any = await dispatch(tuitionInvoiceActions.getByTuitionDetailId(tuitionDetail.id));
        if (result.data?.invoices && result.data.invoices.length > 0) {
          setSelectedInvoiceForView(result.data.invoices[0]);
          setInvoiceDetailModal(true);
        } else {
          dispatch(notificationActions.addNotification('Không tìm thấy hóa đơn!', 'DANGER'));
        }
      }
    } catch (error: any) {
      dispatch(notificationActions.addNotification('Lỗi khi tải thông tin hóa đơn: ' + error.message, 'DANGER'));
    }
  };

  const handleDownloadPDF = async (invoiceId: number) => {
    try {
      // Sử dụng URL trực tiếp thay vì fetchWithAuth để tránh conflict với interceptor
      const { getApiUrl } = await import('../../config/api');
      const token = localStorage.getItem('accessToken') || '';
      const response = await fetch(`${getApiUrl()}/api/tuition-invoices/${invoiceId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': '69420',
        },
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Không tải được PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hoa-don-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('PDF download error:', error);
      dispatch(notificationActions.addNotification('Lỗi khi tải PDF: ' + (error.message || error), 'DANGER'));
    }
  };

  const getSessionsThisMonth = (classId: number, student: any, schedules: any[]) => {
    const classSchedules = schedules.filter((s: any) => s.classId === classId);

    if (classSchedules.length === 0) return 0;

    const currentDate = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDayOfMonth = new Date(selectedYear, selectedMonth, 0).getDate();

    let sessionCount = 0;

    for (let day = 1; day <= lastDayOfMonth; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ...
      const adjustedDay = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert to 1-7 (Monday-Sunday)

      const hasSchedule = classSchedules.some((s: any) => s.dayOfWeek === adjustedDay + 1); // schedules use 2-8

      if (hasSchedule) {
        sessionCount++;
      }
    }

    return sessionCount;
  };

  // Tính lại HP tháng này theo ngày nghỉ giữa tháng và cập nhật DB
  const handleRecalcFee = async () => {
    if (!quitDateForRecalc || selectedTuitionDetails.length !== 1) return;
    const detail = selectedTuitionDetails[0];
    setIsRecalculating(true);
    setRecalcResult(null);
    try {
      // Fetch đồng thời: tất cả attendance sessions và records của học sinh tháng này
      const [allSessions, monthRecords] = await Promise.all([
        attendanceSessionService.getAll().then((res: any) => Array.isArray(res) ? res : []),
        attendanceStudentRecordService.getByMonth(detail.year, detail.month).then((res: any) => Array.isArray(res) ? res : []),
      ]);

      // Lọc records của đúng học sinh
      const studentRecords = monthRecords.filter((r: any) => r.studentId === detail.studentId);

      // Phân loại: ngày đi và ngày nghỉ trong tháng, trước ngày nghỉ hẳn
      const attendedDates: string[] = [];
      const absentDates: string[] = [];
      const monthStart = `${detail.year}-${String(detail.month).padStart(2, '0')}-01`;

      studentRecords.forEach((record: any) => {
        const session = allSessions.find((s: any) => s.id === record.sessionId);
        if (!session) return;
        const sessionDate = new Date(session.date).toISOString().split('T')[0];
        // Chỉ tính buổi trong tháng và trước ngày nghỉ (< quitDate)
        if (sessionDate >= monthStart && sessionDate < quitDateForRecalc) {
          const d = new Date(session.date);
          const weekdays = ['CN', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const label = `${weekdays[d.getDay()]} ${dd}/${mm}`;
          if (record.isPresent) {
            attendedDates.push(label);
          } else {
            absentDates.push(label);
          }
        }
      });

      const attendedCount = attendedDates.length;
      const absentCountThisMonth = absentDates.length;
      const feePerSession = parseFloat(detail.feePerSession || 0);
      const absencesLastMonth = detail.absencesLastMonth || 0;
      const newCurrentMonthFee = attendedCount * feePerSession;
      const newLastMonthAbsenceFee = absencesLastMonth * feePerSession;
      const newTotalFee = newCurrentMonthFee - newLastMonthAbsenceFee;

      // Lưu vào DB (upsert)
      await tuitionDetailService.createOrUpdate({
        studentId: detail.studentId,
        month: detail.month,
        year: detail.year,
        classId: detail.classId,
        sessionsThisMonth: attendedCount,
        feePerSession: detail.feePerSession,
        currentMonthFee: newCurrentMonthFee,
        absencesLastMonth,
        presencesLastMonth: detail.presencesLastMonth || 0,
        lastMonthAbsenceFee: newLastMonthAbsenceFee,
        lastMonthPresenceFee: detail.lastMonthPresenceFee || 0,
        absenceDates: detail.absenceDates, // ngày nghỉ tháng trước (giữ nguyên)
        presenceDates: detail.presenceDates,
        hasExtraFee: detail.hasExtraFee || false,
        extraFeeAmount: detail.extraFeeAmount || 0,
        extraFeeLabel: detail.extraFeeLabel || '',
        includePreviousMonth: detail.includePreviousMonth || false,
        totalFee: newTotalFee,
      });

      // Cập nhật local state để UI phản ánh ngay
      setSelectedTuitionDetails(prev =>
        prev.map(d => d.id === detail.id
          ? { ...d, totalFee: newTotalFee, sessionsThisMonth: attendedCount, currentMonthFee: newCurrentMonthFee }
          : d
        )
      );

      // Lưu chi tiết để hiển thị
      setRecalcResult({ attendedDates, absentDates, attendedCount, absentCountThisMonth, absencesLastMonth, newTotalFee, feePerSession });
      // Reload lại dữ liệu thống kê để phản ánh totalFee mới ngay lập tức
      loadData();
    } catch (err: any) {
      dispatch(notificationActions.addNotification('Lỗi khi tính lại học phí: ' + (err.message || err), 'DANGER'));
    } finally {
      setIsRecalculating(false);
    }
  };

  // Mở modal chọn học sinh — fetch trạng thái HĐ mới nhất cho tất cả học sinh
  const openAutoCalcModal = async () => {
    setAutoCalcModal(true);
    setAutoCalcLoading(true);
    setAutoCalcFilter('');
    try {
      const allStudentsRes = await studentService.getAll();
      const allStudents: any[] = Array.isArray(allStudentsRes) ? allStudentsRes : [];
      const activeStudents = allStudents.filter((s: any) => s.startDate != null);

      // Thêm học sinh đã nghỉ trong tháng/năm đang xem (archivedAt cùng tháng)
      let archivedThisMonth: any[] = [];
      try {
        const archivedRes = await studentService.getArchived();
        const archived: any[] = Array.isArray(archivedRes) ? archivedRes : [];
        archivedThisMonth = archived.filter((s: any) => {
          if (!s.archivedAt) return false;
          const d = new Date(s.archivedAt);
          return d.getMonth() + 1 === Number(selectedMonth) && d.getFullYear() === Number(selectedYear);
        });
      } catch { /* silent */ }

      // Gộp active + archived tháng này (tránh trùng)
      const activeIds = new Set(activeStudents.map((s: any) => s.id));
      const combinedStudents = [
        ...activeStudents,
        ...archivedThisMonth.filter((s: any) => !activeIds.has(s.id)),
      ];

      // Fetch tuition details tháng đang chọn để biết ai có record
      const detailsRes = await tuitionDetailService.getAll({ month: selectedMonth, year: selectedYear });
      const details: any[] = Array.isArray(detailsRes?.data) ? detailsRes.data : (Array.isArray(detailsRes) ? detailsRes : []);
      const detailIds = details.map((d: any) => d.id);

      // Fetch invoices cho tất cả details đó
      let invoicedDetailIds = new Set<number>();
      if (detailIds.length > 0) {
        try {
          const invRes = await tuitionInvoiceService.getByTuitionDetailIds(detailIds);
          const invData = invRes?.data || invRes || {};
          // invData là map detailId -> invoice
          Object.keys(invData).forEach(k => { if (invData[k]) invoicedDetailIds.add(Number(k)); });
        } catch { /* silent */ }
      }

      // Map: studentId -> có invoice không
      const invoicedStudentIds = new Set<number>();
      details.forEach((d: any) => {
        if (invoicedDetailIds.has(d.id)) invoicedStudentIds.add(Number(d.studentId));
      });

      const list = combinedStudents.map(s => ({
        student: s,
        hasInvoice: invoicedStudentIds.has(s.id),
        checked: !invoicedStudentIds.has(s.id), // mặc định chọn những bạn chưa có HĐ
        isArchived: !!s.isArchived, // đánh dấu học sinh đã nghỉ
      }));
      setAutoCalcStudents(list);
    } catch (e: any) {
      dispatch(notificationActions.addNotification('Lỗi khi tải danh sách học sinh: ' + e.message, 'DANGER'));
      setAutoCalcModal(false);
    } finally {
      setAutoCalcLoading(false);
    }
  };

  const handleAutoCalculateTuition = async (studentsToCalc: any[]) => {
    setIsAutoCalculating(true);
    try {
      const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
      const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;

      const [allStudents, allClasses, allAttendanceSessions, previousMonthRecords] = await Promise.all([
        studentService.getAll().then((res: any) => Array.isArray(res) ? res : []),
        classService.getAll().then((res: any) => Array.isArray(res) ? res : []),
        attendanceSessionService.getAll().then((res: any) => Array.isArray(res) ? res : []),
        attendanceStudentRecordService.getByMonth(prevYear, prevMonth).then((res: any) => Array.isArray(res) ? res : []),
      ]);
      dispatch(studentActions.getAll());
      dispatch(classActions.getAll());
      dispatch(scheduleActions.getAll());
      dispatch(attendanceActions.getAllAttendanceSessions());
      dispatch(attendanceStudentRecordActions.getAttendanceStudentRecordByMonth(prevYear, prevMonth));

      // Học sinh đang đi học
      let activeStudents = studentsToCalc;

      if (activeStudents.length === 0) {
        dispatch(notificationActions.addNotification('Không có học sinh nào được chọn để tính!', 'INFO'));
        setIsAutoCalculating(false);
        return;
      }

      // Fetch confirmed sessions
      let confirmedSessionsMap: Record<number, { sessionDate: string; startTime: string; endTime: string }[]> = {};
      try {
        const res = await classMonthlySessionService.getConfirmedByClass(selectedMonth, selectedYear);
        confirmedSessionsMap = res.data || {};
      } catch { confirmedSessionsMap = {}; }

      const confirmedClassIds = new Set(Object.keys(confirmedSessionsMap).map(Number));

      // Kiểm tra lớp chưa xác nhận buổi
      const classIdsWithStudents = new Set(
        activeStudents
          .filter((s: any) => allClasses.find((c: any) => c.id === s.classId && c.sessionFee > 0))
          .map((s: any) => s.classId)
      );
      const unconfirmedClasses: string[] = [];
      classIdsWithStudents.forEach((cId) => {
        if (!confirmedClassIds.has(cId as number)) {
          const cls = allClasses.find((c: any) => c.id === cId);
          if (cls) unconfirmedClasses.push(cls.name);
        }
      });
      if (unconfirmedClasses.length > 0) {
        dispatch(notificationActions.addNotification(
          `Chưa thể tính HP! Các lớp chưa xác nhận tháng ${selectedMonth}/${selectedYear}: ${unconfirmedClasses.join(', ')}. Vào màn hình "Lịch học" → Xác nhận tháng.`,
          'DANGER'
        ));
        setIsAutoCalculating(false);
        return;
      }

      // activeStudents đã được lọc từ modal (chỉ gồm người chưa có HĐ và đã tích chọn)
      const tuitionDetailsToSave = activeStudents.map((student: any) => {
        const studentClass = allClasses.find((cls: any) => cls.id === student.classId);
        if (!studentClass || !studentClass.sessionFee || studentClass.sessionFee <= 0) return null;

        const feePerSession = studentClass.sessionFee || 0;
        const allConfirmedSessions = confirmedSessionsMap[student.classId] || [];

        const isNewStudentThisMonth = (() => {
          if (!student.startDate) return false;
          const sd = new Date(student.startDate);
          return sd.getFullYear() === selectedYear && sd.getMonth() + 1 === selectedMonth;
        })();

        let sessionsThisMonth: number;
        if (isNewStudentThisMonth) {
          const startStr = new Date(student.startDate).toISOString().split('T')[0];
          sessionsThisMonth = allConfirmedSessions.filter((s: any) => {
            const dateStr = s.sessionDate?.split('T')[0] ?? s.sessionDate;
            return dateStr >= startStr;
          }).length;
        } else {
          sessionsThisMonth = allConfirmedSessions.length;
        }

        const absences = previousMonthRecords.filter((r: any) => r.studentId === student.id && r.isPresent === false);
        const presences = previousMonthRecords.filter((r: any) => r.studentId === student.id && r.isPresent === true);
        const absenceDates = absences.map((a: any) => {
          const s = allAttendanceSessions.find((x: any) => x.id === a.sessionId);
          return s ? new Date(s.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : '';
        });
        const presenceDates = presences.map((p: any) => {
          const s = allAttendanceSessions.find((x: any) => x.id === p.sessionId);
          return s ? new Date(s.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : '';
        });

        const currentMonthFee = sessionsThisMonth * feePerSession;
        const lastMonthAbsenceFee = absenceDates.length * feePerSession;
        const lastMonthPresenceFee = presenceDates.length * feePerSession;
        const totalFee = currentMonthFee - lastMonthAbsenceFee;

        return {
          studentId: student.id, month: selectedMonth, year: selectedYear, classId: student.classId,
          sessionsThisMonth, feePerSession, currentMonthFee,
          absencesLastMonth: absenceDates.length, presencesLastMonth: presenceDates.length,
          lastMonthAbsenceFee, lastMonthPresenceFee,
          absenceDates: JSON.stringify(absenceDates), presenceDates: JSON.stringify(presenceDates),
          hasExtraFee: false, extraFeeAmount: 0, extraFeeLabel: '', includePreviousMonth: false, totalFee,
        };
      }).filter((d: any) => d !== null);

      if (tuitionDetailsToSave.length === 0) {
        dispatch(notificationActions.addNotification('Không có học sinh nào có học phí hợp lệ! Kiểm tra học sinh đã xếp lớp và lớp có học phí/buổi.', 'DANGER'));
        setIsAutoCalculating(false);
        return;
      }

      await dispatch(tuitionDetailActions.batchCreateOrUpdate(tuitionDetailsToSave));
      dispatch(notificationActions.addNotification(`✅ Đã tính và lưu HP cho ${tuitionDetailsToSave.length} học sinh tháng ${selectedMonth}/${selectedYear}!`, 'SUCCESS'));
      setAutoCalcModal(false);
      loadData();
    } catch (error: any) {
      console.error('Error auto calculating tuition:', error);
      dispatch(notificationActions.addNotification('Có lỗi xảy ra khi tự động tính học phí: ' + (error.message || error), 'DANGER'));
    } finally {
      setIsAutoCalculating(false);
    }
  };

  // Toggle extra month selection for a student
  const toggleExtraMonth = (studentId: number, tuitionDetailId: number) => {
    setExtraMonthsSelected(prev => {
      const current = prev[studentId] || [];
      const exists = current.includes(tuitionDetailId);
      return {
        ...prev,
        [studentId]: exists
          ? current.filter(id => id !== tuitionDetailId)
          : [...current, tuitionDetailId],
      };
    });
  };

  // ——— Mở modal sửa học phí ———
  const openEditDetail = (detail: any) => {
    setEditingDetail(detail);
    setEditDetailForm({
      sessionsThisMonth: detail.sessionsThisMonth || 0,
      feePerSession: detail.feePerSession || 0,
      currentMonthFee: detail.currentMonthFee || 0,
      absencesLastMonth: detail.absencesLastMonth || 0,
      lastMonthAbsenceFee: detail.lastMonthAbsenceFee || 0,
      hasExtraFee: !!detail.hasExtraFee,
      extraFeeAmount: detail.extraFeeAmount || 0,
      extraFeeLabel: detail.extraFeeLabel || '',
      totalFee: detail.totalFee || 0,
      note: detail.note || '',
    });
    setEditDetailModal(true);
  };

  // Tính lại totalFee khi thay đổi các trường
  const recalcEditDetailTotal = (form: typeof editDetailForm) => {
    const base = form.sessionsThisMonth * form.feePerSession;
    const deduct = form.absencesLastMonth * form.feePerSession;
    const extra = form.hasExtraFee ? form.extraFeeAmount : 0;
    return base - deduct + extra;
  };

  const handleSaveTuitionDetail = async () => {
    if (!editingDetail) return;
    setIsSavingDetail(true);
    try {
      await dispatch(tuitionDetailActions.createOrUpdate({
        ...editingDetail,
        sessionsThisMonth: editDetailForm.sessionsThisMonth,
        feePerSession: editDetailForm.feePerSession,
        currentMonthFee: editDetailForm.sessionsThisMonth * editDetailForm.feePerSession,
        absencesLastMonth: editDetailForm.absencesLastMonth,
        lastMonthAbsenceFee: editDetailForm.absencesLastMonth * editDetailForm.feePerSession,
        hasExtraFee: editDetailForm.hasExtraFee,
        extraFeeAmount: editDetailForm.extraFeeAmount,
        extraFeeLabel: editDetailForm.extraFeeLabel,
        totalFee: editDetailForm.totalFee,
        note: editDetailForm.note.trim() || null,
      }));
      setEditDetailModal(false);
      loadData();
      setInvoiceRefreshKey(k => k + 1); // force badge re-check
    } catch (e: any) {
      dispatch(notificationActions.addNotification('Lỗi khi lưu học phí: ' + e.message, 'DANGER'));
    } finally {
      setIsSavingDetail(false);
    }
  };

  const handleDeleteTuitionDetail = async (detail: any) => {
    const confirmed = window.confirm(
      `Xóa học phí của ${detail.student?.name || 'học sinh này'} tháng ${selectedMonth}/${selectedYear}?\n` +
      `Dầu hiệu dự phòng: Học phí sẽ bị xóa, doanh thu/thống kê sẽ được cập nhật lại.`
    );
    if (!confirmed) return;
    try {
      await dispatch(tuitionDetailActions.deleteDetail(detail.id));
      // Xóa khỏi studentInvoices cache ngay lập tức
      setStudentInvoices(prev => {
        const updated = { ...prev };
        delete updated[detail.studentId];
        return updated;
      });
      loadData();
      setInvoiceRefreshKey(k => k + 1); // force badge re-check
    } catch (e: any) {
      dispatch(notificationActions.addNotification('Lỗi khi xóa: ' + e.message, 'DANGER'));
    }
  };

  // Compute total invoice amount = current months + selected extra months
  const computeInvoiceTotal = () => {
    let total = 0;
    selectedTuitionDetails.forEach((detail: any) => {
      // current month fee
      total += parseFloat(detail.totalFee || 0);
      // extra months
      const extras = extraMonthsSelected[detail.studentId] || [];
      const unpaidList = unpaidMonthsMap[detail.studentId] || [];
      extras.forEach(detailId => {
        const m = unpaidList.find(u => u.tuitionDetailId === detailId);
        if (m) total += m.totalFee;
      });
    });
    return total;
  };

  const handleSaveInvoice = async () => {
    if (selectedTuitionDetails.length === 0) return;

    // Validation: required fields
    if (!invoiceFormData.paymentMethod) {
      dispatch(notificationActions.addNotification('Vui lòng chọn hình thức thanh toán!', 'DANGER'));
      return;
    }
    if (!invoiceFormData.payerName.trim() || invoiceFormData.payerName === '__other__') {
      dispatch(notificationActions.addNotification('Vui lòng chọn hoặc nhập tên người nộp học phí!', 'DANGER'));
      return;
    }

    try {
      let transferImageUrl = '';
      if (invoiceFormData.paymentMethod === 'transfer' && transferImage) {
        const uploadResult = await dispatch(tuitionInvoiceActions.uploadTransferImage(transferImage));
        transferImageUrl = uploadResult.data?.url || '';
      }

      // Build all invoice tasks: 1 per student per month (current + selected extras)
      const invoiceTasks: Array<{ tuitionDetailId: number; studentId: number; amount: number }> = [];
      selectedTuitionDetails.forEach((detail: any) => {
        // Current month
        invoiceTasks.push({
          tuitionDetailId: detail.id,
          studentId: detail.studentId,
          amount: parseFloat(detail.totalFee || 0),
        });
        // Extra months
        const extras = extraMonthsSelected[detail.studentId] || [];
        const unpaidList = unpaidMonthsMap[detail.studentId] || [];
        extras.forEach(detailId => {
          const m = unpaidList.find(u => u.tuitionDetailId === detailId);
          if (m) {
            invoiceTasks.push({
              tuitionDetailId: detailId,
              studentId: detail.studentId,
              amount: m.totalFee,
            });
          }
        });
      });

      // Edit mode: chỉ update hoá đơn đã có
      if (isEditInvoiceMode && currentEditInvoiceId) {
        await dispatch(
          tuitionInvoiceActions.update(currentEditInvoiceId, {
            amount: invoiceFormData.amount,
            paymentDate: invoiceFormData.paymentDate,
            paymentMethod: invoiceFormData.paymentMethod,
            transferImageUrl: invoiceFormData.paymentMethod === 'transfer' ? transferImageUrl : undefined,
            payerName: invoiceFormData.payerName,
            notes: invoiceFormData.notes,
          })
        );
        dispatch(notificationActions.addNotification('Cập nhật hóa đơn thành công!', 'SUCCESS'));
      } else {
        // Create mode: tạo mới tuần tự
        for (const task of invoiceTasks) {
          await dispatch(
            tuitionInvoiceActions.create({
              tuitionDetailId: task.tuitionDetailId,
              studentId: task.studentId,
              amount: task.amount,
              paymentDate: invoiceFormData.paymentDate,
              paymentMethod: invoiceFormData.paymentMethod,
              transferImageUrl: invoiceFormData.paymentMethod === 'transfer' ? transferImageUrl : undefined,
              payerName: invoiceFormData.payerName,
              notes: invoiceFormData.notes,
            })
          );
        }
        const totalInvoices = invoiceTasks.length;
        dispatch(notificationActions.addNotification(totalInvoices === 1 ? 'Tạo hóa đơn thành công!' : `Tạo thành công ${totalInvoices} hóa đơn!`, 'SUCCESS'));
      }
      toggleInvoiceModal();
      // Reset để useEffect tự fetch lại invoice cho trang hiện tại
      setStudentInvoices({});
      setInvoiceRefreshKey(k => k + 1);
      loadData();
    } catch (error: any) {
      dispatch(notificationActions.addNotification('Lỗi khi tạo hóa đơn: ' + error.message, 'DANGER'));
    }
  };

  const formatCurrency = (amount: number) => {
    return amount?.toLocaleString('vi-VN') || '0';
  };

  const handleDownloadXlsx = () => {
    const FEE_PER_SESSION = 55000;

    const sorted = [...filteredTuitionDetails].sort((a: any, b: any) => {
      const clsA = classes.find((c: any) => c.id === a.classId)?.name || '';
      const clsB = classes.find((c: any) => c.id === b.classId)?.name || '';
      return clsA.localeCompare(clsB);
    });

    const rows = sorted.map((detail: any, idx: number) => {
      // Look up full student from Redux (has address)
      const student = students.find((s: any) => s.id === detail.studentId) || detail.student || {};

      // Address: use student.address or fallback to random "Thôn X"
      const randomThon = Math.floor(Math.random() * 10) + 1;
      const address = student.address || `Thôn ${randomThon}`;

      // Sessions = totalFee / feePerSession (round to nearest int)
      const totalFee = Math.round(detail.totalFee || 0);
      const sessions = detail.feePerSession
        ? Math.round(totalFee / detail.feePerSession)
        : Math.round(totalFee / FEE_PER_SESSION);

      return {
        'STT': idx + 1,
        'Tên học sinh': student.name || detail.student?.name || '',
        'Lớp': classes.find((c: any) => c.id === detail.classId)?.name || '',
        'Địa chỉ': address,
        'Số buổi học': sessions,
        [`HP tháng ${selectedMonth}/${selectedYear}`]: totalFee,
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 6 },   // STT
      { wch: 28 },  // Tên học sinh
      { wch: 14 },  // Lớp (trống)
      { wch: 30 },  // Địa chỉ
      { wch: 14 },  // Số buổi học
      { wch: 20 },  // HP tháng
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Học phí');
    XLSX.writeFile(wb, `hoc-phi-thang-${selectedMonth}-${selectedYear}.xlsx`);
  };

  const MONTH_NAMES = [
    'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
    'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
  ];

  return (
    <PageWrapper>
      <Container fluid style={{ padding: '0 0 30px' }}>
        {/* ── Premium Page Header ── */}
        <PageHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {/* Title */}
            <PageTitle style={{ margin: 0, fontSize: '1.25rem', whiteSpace: 'nowrap' }}>
              <span>📊</span> Thống kê học phí
            </PageTitle>

            {/* Divider */}
            <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />

            {/* Tháng */}
            <FilterGroup>
              <FilterLabel>Tháng</FilterLabel>
              <StyledSelect
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {MONTH_NAMES.map((month, index) => (
                  <option key={index + 1} value={index + 1}>{month}</option>
                ))}
              </StyledSelect>
            </FilterGroup>

            {/* Năm */}
            <FilterGroup>
              <FilterLabel>Năm</FilterLabel>
              <StyledSelect
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {[2024, 2025, 2026].map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </StyledSelect>
            </FilterGroup>

            {/* Spacer */}
            <div style={{ flex: 1, minWidth: 8 }} />

            {/* Action button */}
            <ActionBtn
              variant="success"
              onClick={openAutoCalcModal}
              disabled={isAutoCalculating}
            >
              {isAutoCalculating ? '⏳ Đang tính...' : '⚡ Tự động tính HP'}
            </ActionBtn>
          </div>
        </PageHeader>

        <div style={{ padding: '0 20px' }}>

          {/* ── Modal Tự động tính HP ── */}
          <Modal isOpen={autoCalcModal} toggle={() => !isAutoCalculating && setAutoCalcModal(false)} size="lg">
            <ModalHeader toggle={() => !isAutoCalculating && setAutoCalcModal(false)}>
              ⚡ Tự động tính học phí tháng {selectedMonth}/{selectedYear}
            </ModalHeader>
            <ModalBody>
              {/* Công thức */}
              <div style={{ background: '#e8f5e9', border: '1.5px solid #a5d6a7', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.87rem' }}>
                <div style={{ fontWeight: 700, color: '#2e7d32', marginBottom: 4 }}>📐 Công thức tính:</div>
                <div>HP = (Số buổi đã xác nhận × Phí/buổi) − (Số buổi nghỉ T{selectedMonth === 1 ? 12 : selectedMonth - 1} × Phí/buổi)</div>
                <div style={{ color: '#555', marginTop: 3, fontSize: '0.8rem' }}>⚠️ Học sinh đã có hoá đơn sẽ không được tính lại.</div>
              </div>

              {autoCalcLoading ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>⏳ Đang tải danh sách học sinh...</div>
              ) : (() => {
                const filtered = autoCalcFilter.trim()
                  ? autoCalcStudents.filter(x => x.student.name?.toLowerCase().includes(autoCalcFilter.toLowerCase()))
                  : autoCalcStudents;
                const withInvoice = autoCalcStudents.filter(x => x.hasInvoice);
                const withoutInvoice = autoCalcStudents.filter(x => !x.hasInvoice);
                const checkedCount = autoCalcStudents.filter(x => x.checked && !x.hasInvoice).length;
                const allChecked = withoutInvoice.length > 0 && withoutInvoice.every(x => x.checked);
                return (
                  <>
                    {/* Summary */}
                    <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                      <span style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: 20, padding: '3px 12px', fontSize: '0.82rem', fontWeight: 600 }}>
                        👥 Tổng: {autoCalcStudents.length} học sinh
                      </span>
                      <span style={{ background: '#fce4ec', color: '#c62828', borderRadius: 20, padding: '3px 12px', fontSize: '0.82rem', fontWeight: 600 }}>
                        ✅ Đã có HĐ: {withInvoice.length}
                      </span>
                      <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 20, padding: '3px 12px', fontSize: '0.82rem', fontWeight: 600 }}>
                        ✔️ Sẽ tính: {checkedCount}
                      </span>
                    </div>

                    {/* Search + select all */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                      <input
                        type="text" placeholder="🔍 Tìm học sinh..."
                        value={autoCalcFilter}
                        onChange={e => setAutoCalcFilter(e.target.value)}
                        style={{ flex: 1, padding: '5px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: '0.85rem' }}
                      />
                      <button
                        onClick={() => setAutoCalcStudents(prev => prev.map(x =>
                          x.hasInvoice ? x : { ...x, checked: !allChecked }
                        ))}
                        style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #90caf9', background: allChecked ? '#bbdefb' : '#fff', fontSize: '0.82rem', cursor: 'pointer' }}
                      >
                        {allChecked ? '☑ Bỏ tất cả' : '☐ Chọn tất cả chưa có HĐ'}
                      </button>
                    </div>

                    {/* Student list */}
                    <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8 }}>
                      {withInvoice.length > 0 && (
                        <div style={{ background: '#fafafa', padding: '6px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '0.78rem', color: '#888', fontWeight: 600 }}>
                          ✅ Đã có hoá đơn — không tính lại ({withInvoice.length})
                        </div>
                      )}
                      {autoCalcStudents
                        .filter(x => x.hasInvoice)
                        .filter(x => !autoCalcFilter.trim() || x.student.name?.toLowerCase().includes(autoCalcFilter.toLowerCase()))
                        .map(x => (
                          <div key={x.student.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', borderBottom: '1px solid #f5f5f5', background: '#fafafa', opacity: 0.65 }}>
                            <input type="checkbox" disabled checked={false} />
                            <span style={{ fontSize: '0.87rem', color: '#666' }}>{x.student.name}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#e53935', fontWeight: 600 }}>⛔ Đã có HĐ</span>
                          </div>
                        ))
                      }
                      {withoutInvoice.length > 0 && (
                        <div style={{ background: '#f1f8e9', padding: '6px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '0.78rem', color: '#558b2f', fontWeight: 600 }}>
                          📋 Chưa có hoá đơn — có thể tính ({withoutInvoice.length})
                        </div>
                      )}
                      {autoCalcStudents
                        .filter(x => !x.hasInvoice)
                        .filter(x => !autoCalcFilter.trim() || x.student.name?.toLowerCase().includes(autoCalcFilter.toLowerCase()))
                        .map(x => (
                          <div
                            key={x.student.id}
                            onClick={() => setAutoCalcStudents(prev => prev.map(p => p.student.id === x.student.id ? { ...p, checked: !p.checked } : p))}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: x.checked ? '#e8f5e9' : '#fff', transition: 'background 0.15s' }}
                          >
                            <input type="checkbox" checked={x.checked} readOnly style={{ accentColor: '#2e7d32', width: 15, height: 15 }} />
                            <span style={{ fontSize: '0.87rem', fontWeight: x.checked ? 600 : 400 }}>{x.student.name}</span>
                            {(x as any).isArchived && (
                              <span style={{ fontSize: '0.7rem', background: '#ffcdd2', color: '#b71c1c', borderRadius: 8, padding: '1px 7px', fontWeight: 700 }}>Đã nghỉ</span>
                            )}
                            {(() => { const c = classes.find((cl: any) => cl.id === x.student.classId); return c ? <span style={{ fontSize: '0.75rem', color: '#6366f1', marginLeft: 4 }}>({c.name})</span> : null; })()}
                          </div>
                        ))
                      }
                      {filtered.length === 0 && <div style={{ padding: '14px', color: '#9e9e9e', textAlign: 'center', fontSize: '0.85rem' }}>Không tìm thấy.</div>}
                    </div>
                  </>
                );
              })()}
            </ModalBody>
            <ModalFooter style={{ gap: 8 }}>
              <button
                onClick={() => setAutoCalcModal(false)}
                disabled={isAutoCalculating}
                style={{ padding: '6px 18px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: '0.88rem' }}
              >
                Hủy
              </button>
              <button
                disabled={isAutoCalculating || autoCalcLoading || autoCalcStudents.filter(x => x.checked && !x.hasInvoice).length === 0}
                onClick={() => {
                  const selected = autoCalcStudents.filter(x => x.checked && !x.hasInvoice).map(x => x.student);
                  handleAutoCalculateTuition(selected);
                }}
                style={{
                  padding: '6px 20px', borderRadius: 6, border: 'none', fontSize: '0.88rem', fontWeight: 700,
                  background: isAutoCalculating ? '#9e9e9e' : '#2e7d32', color: '#fff',
                  cursor: isAutoCalculating ? 'not-allowed' : 'pointer',
                }}
              >
                {isAutoCalculating ? '⏳ Đang tính...' : `⚡ Tính HP cho ${autoCalcStudents.filter(x => x.checked && !x.hasInvoice).length} học sinh`}
              </button>
            </ModalFooter>
          </Modal>

          {/* KPI Cards */}
          {invoiceStatistics && (
            <KpiRow>
              <KpiCard accent="#1976d2">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <KpiLabel style={{ margin: 0 }}>Tổng học sinh</KpiLabel>
                  <KpiValue color="#1565c0" style={{ fontSize: '1.05rem' }}>{invoiceStatistics.overview?.totalStudents || 0}</KpiValue>
                </div>
              </KpiCard>

              <KpiCard accent="#2e7d32">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <KpiLabel style={{ margin: 0 }}>Tổng doanh thu</KpiLabel>
                  <KpiValue
                    color="#2e7d32"
                    style={{ fontSize: '1.05rem' }}
                    onClick={() => setShowRevenue(!showRevenue)}
                  >
                    {showRevenue ? <>{formatCurrency(invoiceStatistics.overview?.totalRevenue || 0)} đ</> : <>●●●●●●</>}
                    <span style={{ fontSize: '0.85rem' }}>{showRevenue ? '👁️' : '👁️‍🗨️'}</span>
                  </KpiValue>
                </div>
              </KpiCard>

              <KpiCard accent="#0288d1">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <KpiLabel style={{ margin: 0 }}>Đã thu tháng này</KpiLabel>
                  <KpiValue
                    color="#0277bd"
                    style={{ fontSize: '1.05rem' }}
                    onClick={() => setShowPaid(!showPaid)}
                  >
                    {showPaid ? <>{formatCurrency(invoiceStatistics.overview?.totalCollectedThisMonth || 0)} đ</> : <>●●●●●●</>}
                    <span style={{ fontSize: '0.85rem' }}>{showPaid ? '👁️' : '👁️‍🗨️'}</span>
                  </KpiValue>
                </div>
                {showPaid && (
                  <div style={{ marginTop: 6, fontSize: '0.73rem', color: '#546e7a', lineHeight: 1.5, borderTop: '1px solid #e0f2f1', paddingTop: 5 }}>
                    <div>
                      📍 HP T{selectedMonth}/{selectedYear}: <strong style={{ color: '#2e7d32' }}>{formatCurrency((invoiceStatistics.overview?.totalCollectedThisMonth || 0) - (invoiceStatistics.overview?.totalPrevMonthsCollected || 0))} đ</strong>
                      <span style={{ color: '#90a4ae', fontSize: '0.68rem', marginLeft: 4 }}>(Chỉ tính các khoản thu trong T{selectedMonth}/{selectedYear} này)</span>
                    </div>
                    {(invoiceStatistics.overview?.totalPrevMonthsCollected || 0) > 0 && (
                      <div>📊 Thu bù t.trước: <strong style={{ color: '#e65100' }}>{formatCurrency(invoiceStatistics.overview?.totalPrevMonthsCollected || 0)} đ</strong></div>
                    )}
                  </div>
                )}
              </KpiCard>

              <KpiCard accent="#c62828">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <KpiLabel style={{ margin: 0 }}>Chưa thu</KpiLabel>
                  <KpiValue
                    color="#c62828"
                    style={{ fontSize: '1.05rem' }}
                    onClick={() => setShowUnpaid(!showUnpaid)}
                  >
                    {showUnpaid ? <>{formatCurrency(invoiceStatistics.overview?.totalUnpaid || 0)} đ</> : <>●●●●●●</>}
                    <span style={{ fontSize: '0.85rem' }}>{showUnpaid ? '👁️' : '👁️‍🗨️'}</span>
                  </KpiValue>
                </div>
              </KpiCard>
            </KpiRow>
          )}

          {/* ── Chi tiết học phí ── */}
          <ContentCard>
            <SectionTitle>
              <span>📋</span> Chi tiết học phí tháng {selectedMonth}/{selectedYear}
            </SectionTitle>

            {/* Search + Teacher filter + Download */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '18px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>&#128269; Tìm kiếm học sinh</div>
                <SearchInput
                  type="text"
                  placeholder="Nhập tên học sinh..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>&#128105;&#8205;&#127979; Giáo viên</div>
                <NativeSelect
                  value={filterTeacherId}
                  onChange={(e) => setFilterTeacherId(e.target.value)}
                >
                  <option value="">Tất cả giáo viên</option>
                  {Array.isArray(teachers) && [...teachers]
                    .filter((t: any) => classes.some((c: any) => c.teacherId === t.id))
                    .sort((a: any, b: any) => (a.userName || '').localeCompare(b.userName || ''))
                    .map((t: any) => (
                      <option key={t.id} value={t.id}>{t.userName}</option>
                    ))}
                </NativeSelect>
              </div>
              <div style={{ flex: '1 1 180px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>&#127974; Ngân hàng</div>
                <NativeSelect
                  value={filterBankAccountId}
                  onChange={(e) => { setFilterBankAccountId(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">Tất cả ngân hàng</option>
                  {bankAccounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.bankName} — {acc.accountNumber}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <button
                onClick={handleDownloadXlsx}
                style={{
                  padding: '7px 16px',
                  background: 'linear-gradient(135deg, #217346 0%, #1e8449 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  alignSelf: 'flex-end',
                }}
                title={`Tải Excel ${filteredTuitionDetails.length} học sinh đang hiển thị`}
              >
                📥 Tải XLSX ({filteredTuitionDetails.length})
              </button>
            </div>

            {/* Multi-select action bar */}
            {selectedStudentsForInvoice.length > 0 && (
              <div
                style={{
                  backgroundColor: '#e3f2fd',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                }}
              >
                {/* Row 1: Count + action buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                    Đã chọn {selectedStudentsForInvoice.length} học sinh
                  </span>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {(() => {
                      // Phân biệt 3 trạng thái:
                      //   undefined = chưa load xong (đang fetch)
                      //   null      = đã check, không có HĐ
                      //   object    = đã có HĐ
                      const stillLoading = selectedStudentsForInvoice.filter(sid => !(String(sid) in Object.fromEntries(Object.entries(studentInvoices).map(([k, v]) => [String(k), v]))) && !(Number(sid) in studentInvoices));
                      const alreadyInvoiced = selectedStudentsForInvoice.filter(sid => {
                        const inv = studentInvoices[Number(sid)];
                        return inv !== undefined && inv !== null;
                      });
                      const canCreate = selectedStudentsForInvoice.filter(sid => {
                        if (!(Number(sid) in studentInvoices)) return false; // chưa load xong → chưa biết
                        const inv = studentInvoices[Number(sid)];
                        return inv === null; // đã check, không có HĐ
                      });
                      const isLoadingInvoices = selectedStudentsForInvoice.some(sid => !(Number(sid) in studentInvoices));
                      return (
                        <>
                          {alreadyInvoiced.length > 0 && (
                            <span style={{ fontSize: '0.8rem', color: '#e65100', background: '#fff3e0', borderRadius: 6, padding: '3px 8px' }}>
                              ⚠️ {alreadyInvoiced.length} học sinh đã có hoá đơn (bị bỏ qua)
                            </span>
                          )}
                          {isLoadingInvoices && (
                            <span style={{ fontSize: '0.8rem', color: '#666', padding: '3px 8px' }}>
                              ⏳ Đang kiểm tra...
                            </span>
                          )}
                          <Button
                            size="sm"
                            color="success"
                            onClick={handleCreateMultipleInvoice}
                            disabled={canCreate.length === 0 || isLoadingInvoices}
                          >
                            💰 Tạo hóa đơn chung ({canCreate.length})
                          </Button>
                        </>
                      );
                    })()}
                    <Button size="sm" color="secondary" onClick={() => setSelectedStudentsForInvoice([])}>
                      Bỏ chọn tất cả
                    </Button>
                  </div>
                </div>
                {/* Row 2: Selected student name tags */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {selectedStudentsForInvoice.map((sid) => {
                    const detail = tuitionDetails.find((d: any) => String(d.studentId) === sid);
                    const name = detail?.student?.name || `#${sid}`;
                    const cls = classes.find((c: any) => c.id === detail?.classId);
                    const className = cls?.name || '';
                    return (
                      <span
                        key={sid}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          backgroundColor: '#fff',
                          border: '1.5px solid #90caf9',
                          borderRadius: '20px',
                          padding: '3px 10px 3px 12px',
                          fontSize: '0.82rem',
                          fontWeight: 500,
                          color: '#1565c0',
                        }}
                      >
                        {name}{className && <span style={{ fontSize: '0.75rem', color: '#90caf9', marginLeft: 4 }}>({className})</span>}
                        <button
                          onClick={() => toggleStudentForInvoice(sid)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0 2px',
                            color: '#42a5f5',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            lineHeight: 1,
                          }}
                          title={`Bỏ chọn ${name}`}
                        >
                          ✕
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Summary thanh toán (copy từ Tab 2 — hiển thị đủ thông tin ngay ở tab Chi tiết) ── */}
            {invoiceStatistics && (() => {
              const unpaidList: any[] = [
                ...(invoiceStatistics.students?.unpaid || []),
                ...(invoiceStatistics.students?.partiallyPaid || []),
              ];
              const paidList: any[] = invoiceStatistics.students?.paid || [];
              const totalUnpaidAmount = unpaidList.reduce((sum: number, s: any) => sum + Number(s.totalFee || s.unpaid || 0), 0);
              const totalPaidAmount = paidList.reduce((sum: number, s: any) => sum + Number(s.paidAmount || s.totalPaid || 0), 0);
              return (
                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 8, padding: '6px 14px', flex: 1, minWidth: 160, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '0.76rem', color: '#c62828', fontWeight: 700, whiteSpace: 'nowrap' }}>⏳ Chưa TT</span>
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: '#c62828' }}>{unpaidList.length} HS</span>
                    <span style={{ fontSize: '0.82rem', color: '#e53935', marginLeft: 'auto' }}>{formatCurrency(totalUnpaidAmount)} đ</span>
                  </div>
                  <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '6px 14px', flex: 1, minWidth: 160, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '0.76rem', color: '#2e7d32', fontWeight: 700, whiteSpace: 'nowrap' }}>✅ Đã TT (Tính cả khoản thu bù vào tháng sau)</span>
                    <span style={{ fontSize: '1rem', fontWeight: 700, color: '#2e7d32' }}>{paidList.length} HS</span>
                    <span style={{ fontSize: '0.82rem', color: '#43a047', marginLeft: 'auto' }}>{formatCurrency(totalPaidAmount)} đ</span>
                  </div>
                </div>
              );
            })()}

            {loading ? (
              <p>Đang tải dữ liệu...</p>
            ) : (
              <div style={{ position: 'relative', maxHeight: '80vh', overflowY: 'auto' }}>
                <Table striped style={{ marginTop: 0, marginBottom: 0, width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '50px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <Input
                            type="checkbox"
                            checked={
                              filteredTuitionDetails.length > 0 &&
                              selectedStudentsForInvoice.length === filteredTuitionDetails.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStudentsForInvoice(
                                  filteredTuitionDetails.map((d: any) => String(d.studentId))
                                );
                              } else {
                                setSelectedStudentsForInvoice([]);
                              }
                            }}
                          />
                        </div>
                      </th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '40px' }}>#</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, minWidth: '120px' }}>Học sinh</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '80px' }}>Lớp</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '100px' }}>Số buổi tháng này (T{selectedMonth})</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '100px' }}>Số buổi nghỉ tháng trước (T{previousMonth})</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '100px' }}>Học phí/buổi</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '110px' }}>Học phí tháng này</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '100px' }}>Phụ phí</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '110px' }}>Tổng cộng</th>
                      <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', border: '0.5px solid #ccc', padding: '8px', textAlign: 'center', fontWeight: 600, width: '150px' }}>Hành động</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ textAlign: 'center', color: '#999' }}>
                          {searchName.trim() ? 'Không tìm thấy học sinh phù hợp' : 'Chưa có dữ liệu học phí cho tháng này'}
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((detail: any, index: number) => (
                        <tr key={detail.id} style={{
                          background: studentInvoices[detail.studentId]
                            ? 'rgba(232,245,233,0.6)'   // xanh nhạt = đã TT
                            : 'rgba(255,235,238,0.5)',   // đỏ nhạt = chưa TT
                        }}>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '8px' }}>
                            <Input
                              type="checkbox"
                              checked={selectedStudentsForInvoice.includes(String(detail.studentId))}
                              onChange={() => toggleStudentForInvoice(String(detail.studentId))}
                            />
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{indexOfFirstItem + index + 1}</td>
                          <td style={{ padding: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 600, color: studentInvoices[detail.studentId] ? '#2e7d32' : '#c62828' }}>
                                {detail.student?.name || 'N/A'}
                              </span>
                              {studentInvoices[detail.studentId]
                                ? <span style={{ fontSize: '0.68rem', background: '#c8e6c9', color: '#1b5e20', borderRadius: 10, padding: '1px 6px', fontWeight: 700, whiteSpace: 'nowrap' }}>✅ Đã TT</span>
                                : <span style={{ fontSize: '0.68rem', background: '#ffcdd2', color: '#b71c1c', borderRadius: 10, padding: '1px 6px', fontWeight: 700, whiteSpace: 'nowrap' }}>⏳ Chưa TT</span>
                              }
                              {detail.student?.isArchived && (
                                <span style={{ fontSize: '0.68rem', background: '#fff3e0', color: '#e65100', borderRadius: 10, padding: '1px 6px', fontWeight: 700, whiteSpace: 'nowrap', border: '1px solid #ffcc80' }}>ĐÃ NGHỈ</span>
                              )}
                              {detail.note && (
                                <span
                                  title={detail.note}
                                  style={{ fontSize: '0.68rem', background: '#e3f2fd', color: '#1565c0', borderRadius: 10, padding: '1px 6px', fontWeight: 700, whiteSpace: 'nowrap', border: '1px solid #90caf9', cursor: 'help' }}
                                >✏️ Đã chỉnh sửa HP</span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.class?.name || 'N/A'}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.sessionsThisMonth}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {detail.absencesLastMonth || 0}
                            {(() => {
                              const dates: string[] = (() => { try { return JSON.parse(detail.absenceDates || '[]'); } catch { return []; } })();
                              if (dates.length === 0) return null;
                              return (
                                <div style={{ fontSize: '0.75rem', color: '#c62828', marginTop: 3 }}>
                                  {dates.map(d => d.replace(/-/g, '/')).join('; ')}
                                </div>
                              );
                            })()}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.feePerSession)} đ</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.currentMonthFee)} đ</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            {detail.hasExtraFee ? (
                              <>
                                {formatCurrency(detail.extraFeeAmount)} đ
                                {detail.extraFeeLabel && (
                                  <small style={{ display: 'block', color: '#6c757d' }}>
                                    ({detail.extraFeeLabel})
                                  </small>
                                )}
                              </>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            <strong>{formatCurrency(detail.totalFee)} đ</strong>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {/* Nhóm nút HOÁ ĐƠN */}
                              {studentInvoices[detail.studentId] ? (
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  <Button
                                    size="sm"
                                    color="warning"
                                    onClick={() => handleEditInvoice(detail)}
                                  >
                                    ✏️ Sửa
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="info"
                                    onClick={() => handleDownloadPDF(studentInvoices[detail.studentId].id)}
                                  >
                                    📄 Tải
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  color="success"
                                  onClick={() => handleCreateInvoice(detail)}
                                >
                                  Tạo hóa đơn
                                </Button>
                              )}

                              {/* Nhóm nút SỬa HP / Xóa HP — chỉ khi chưa có hoá đơn */}
                              {!studentInvoices[detail.studentId] && (
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <Button
                                    size="sm"
                                    color="secondary"
                                    outline
                                    onClick={() => openEditDetail(detail)}
                                    title="Sửa học phí đã tính"
                                  >
                                    ✏️ Sửa HP
                                  </Button>
                                  <Button
                                    size="sm"
                                    color="danger"
                                    outline
                                    onClick={() => handleDeleteTuitionDetail(detail)}
                                    title="Xóa dòng học phí này"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Tổng học phí của tất cả học sinh đang hiển thị */}
            {filteredTuitionDetails.length > 0 && (
              <div style={{
                display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                gap: 12, marginTop: 8, marginBottom: 4,
                background: 'linear-gradient(135deg,#1e3a5f,#2563eb)',
                borderRadius: 10, padding: '10px 20px',
              }}>
                <span style={{ color: '#93c5fd', fontSize: '0.9rem' }}>
                  Tổng học phí <strong style={{ color: '#fff' }}>{filteredTuitionDetails.length}</strong> học sinh
                  {filterBankAccountId && bankAccounts.find(b => String(b.id) === filterBankAccountId) && (
                    <span style={{ color: '#fbbf24', marginLeft: 6 }}>
                      ({bankAccounts.find(b => String(b.id) === filterBankAccountId)?.bankName})
                    </span>
                  )}:
                </span>
                <span style={{ fontWeight: 800, fontSize: '1.15rem', color: '#34d399' }}>
                  {formatCurrency(filteredTuitionDetails.reduce((sum: number, d: any) => sum + parseFloat(d.totalFee || 0), 0))} đ
                </span>
              </div>
            )}

            {/* Pagination */}
            {filteredTuitionDetails.length > 0 && (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#6c757d' }}>
                  Hiển thị {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredTuitionDetails.length)} trong tổng số {filteredTuitionDetails.length} học sinh
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    ««
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    ‹ Trước
                  </Button>
                  <span style={{ padding: '0 15px', lineHeight: '31px' }}>
                    Trang {currentPage} / {totalPages}
                  </span>
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Sau ›
                  </Button>
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    »»
                  </Button>
                </div>
              </div>
            )}
          </ContentCard>

        </div> {/* end padding div */}

        {/* Sibling Confirmation Modal */}
        {siblingConfirmTarget && (() => {
          const detail = siblingConfirmTarget;
          const siblingIds = siblingsMap[detail.studentId] || [];
          const siblingDetails = tuitionDetails.filter((d: any) => siblingIds.includes(d.studentId));
          const siblingNames = siblingDetails.map((d: any) => d.student?.name).filter(Boolean).join(', ');
          const studentName = detail.student?.name || `HS #${detail.studentId}`;
          return (
            <Modal isOpen={!!siblingConfirmTarget} toggle={() => setSiblingConfirmTarget(null)} centered>
              <ModalHeader toggle={() => setSiblingConfirmTarget(null)}>
                👨‍👩‍👧 Tạo hóa đơn — có anh/chị/em
              </ModalHeader>
              <ModalBody>
                <p style={{ marginBottom: 12 }}>
                  Học sinh <strong>{studentName}</strong> có anh/chị/em chưa nộp học phí:
                </p>
                <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '0.9rem' }}>
                  {siblingDetails.map((d: any) => (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>👤 {d.student?.name}</span>
                      <span style={{ color: '#e53935', fontWeight: 600 }}>{(d.totalFee || 0).toLocaleString('vi-VN')} đ</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: '0.85rem', color: '#555' }}>Bạn muốn tạo hóa đơn nào?</p>
              </ModalBody>
              <ModalFooter style={{ gap: 8 }}>
                <Button color="secondary" outline onClick={() => setSiblingConfirmTarget(null)}>Hủy</Button>
                <Button color="primary" outline onClick={async () => {
                  const d = siblingConfirmTarget;
                  setSiblingConfirmTarget(null);
                  await _openCreateInvoiceModal([d]);
                }}>
                  Tạo riêng cho {detail.student?.name || 'học sinh này'}
                </Button>
                <Button color="success" onClick={async () => {
                  const d = siblingConfirmTarget;
                  const allDetails = [d, ...siblingDetails];
                  setSiblingConfirmTarget(null);
                  await _openCreateInvoiceModal(allDetails);
                }}>
                  👨‍👩‍👧 Tạo cả nhóm ({siblingDetails.length + 1} HS)
                </Button>
              </ModalFooter>
            </Modal>
          );
        })()}

        {/* Invoice Creation Modal */}
        <Modal isOpen={invoiceModal} toggle={toggleInvoiceModal} size="lg">
          <ModalHeader toggle={toggleInvoiceModal}>
            {selectedTuitionDetails.length === 1
              ? 'Tạo hóa đơn thanh toán'
              : `Tạo hóa đơn cho ${selectedTuitionDetails.length} học sinh`}
          </ModalHeader>
          <ModalBody>
            {selectedTuitionDetails.length > 0 && (
              <>
                {/* ── Section: Student info & current month fee ── */}
                {selectedTuitionDetails.length === 1 ? (
                  <>
                    <p><strong>Học sinh:</strong> {selectedTuitionDetails[0].student?.name}
                      {(() => { const c = classes.find((cl: any) => cl.id === selectedTuitionDetails[0].classId); return c ? <span style={{ fontSize: '0.85rem', color: '#6366f1', marginLeft: 6 }}>({c.name})</span> : null; })()}
                    </p>
                  </>
                ) : (
                  <p><strong>Số học sinh:</strong> {selectedTuitionDetails.length}</p>
                )}


                {/* ── Section: Nghỉ giữa tháng: chỉ hiện khi 1 học sinh ── */}
                {selectedTuitionDetails.length === 1 && (
                  <div style={{
                    background: '#fff8f0',
                    border: `1.5px solid ${quitDateForRecalc ? '#fb923c' : '#fed7aa'}`,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    marginBottom: '14px',
                  }}>
                    <div style={{ fontWeight: 600, color: '#c2410c', marginBottom: '8px', fontSize: '0.88rem' }}>
                      🚪 Nghỉ hẳn giữa tháng (tính lại HP)
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ fontSize: '0.85rem', color: '#7c3aed', fontWeight: 600, whiteSpace: 'nowrap', margin: 0 }}>
                        🗓️ Nghỉ từ:
                      </label>
                      <input
                        type="date"
                        value={quitDateForRecalc}
                        min={`${selectedTuitionDetails[0].year}-${String(selectedTuitionDetails[0].month).padStart(2, '0')}-01`}
                        max={`${selectedTuitionDetails[0].year}-${String(selectedTuitionDetails[0].month).padStart(2, '0')}-${new Date(selectedTuitionDetails[0].year, selectedTuitionDetails[0].month, 0).getDate()}`}
                        onChange={e => setQuitDateForRecalc(e.target.value)}
                        style={{
                          padding: '4px 10px', borderRadius: 6, fontSize: '0.88rem',
                          border: `1.5px solid ${quitDateForRecalc ? '#fb923c' : '#d1d5db'}`,
                          outline: 'none',
                        }}
                      />
                      {quitDateForRecalc && (
                        <>
                          <button
                            onClick={handleRecalcFee}
                            disabled={isRecalculating}
                            style={{
                              background: isRecalculating ? '#9ca3af' : '#ea580c',
                              color: '#fff', border: 'none', borderRadius: 6,
                              padding: '5px 14px', fontSize: '0.85rem',
                              cursor: isRecalculating ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            {isRecalculating ? '⏳ Đang tính...' : '🔄 Tính lại HP tháng này'}
                          </button>
                          <button
                            onClick={() => setQuitDateForRecalc('')}
                            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.85rem' }}
                          >✕ Bỏ</button>
                        </>
                      )}
                    </div>
                    {quitDateForRecalc && (
                      <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#78350f' }}>
                        ⚠️ Nếu nhấn "🔄 Tính lại", học phí sẽ được <strong>cập nhật vào DB</strong> trước khi tạo HĐ.
                      </div>
                    )}
                    {!quitDateForRecalc && (
                      <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic' }}>
                        Để trống nếu học sinh học đủ cả tháng — HP giữ nguyên.
                      </div>
                    )}
                  </div>
                )}

                {/* ── Section: Month selection ── */}
                <div style={{
                  background: '#f8faff',
                  border: '1.5px solid #c7d2fe',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  marginBottom: '16px',
                }}>
                  <div style={{ fontWeight: 600, color: '#3730a3', marginBottom: '10px', fontSize: '0.9rem' }}>
                    📅 Tháng học phí đang thu:
                  </div>

                  {/* Current month rows (always checked, cannot uncheck) */}
                  {selectedTuitionDetails.map((detail: any) => (
                    <div key={`cur-${detail.id}`}>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '6px 10px', borderRadius: '8px',
                        background: '#e0e7ff', marginBottom: recalcResult && selectedTuitionDetails.length === 1 ? '4px' : '6px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '1rem' }}>✅</span>
                          <span style={{ fontWeight: 600, color: '#3730a3' }}>
                            T{detail.month}/{detail.year}
                            {selectedTuitionDetails.length > 1 && (
                              <span style={{ fontWeight: 400, color: '#6366f1', marginLeft: '6px', fontSize: '0.83rem' }}>
                                — {detail.student?.name}
                                {(() => { const c = classes.find((cl: any) => cl.id === detail.classId); return c ? <span style={{ color: '#a5b4fc', marginLeft: 4 }}>({c.name})</span> : null; })()}
                              </span>
                            )}
                          </span>
                        </div>
                        <span style={{ fontWeight: 700, color: '#4338ca' }}>
                          {formatCurrency(parseFloat(detail.totalFee || 0))} đ
                        </span>
                      </div>
                      {/* Chi tiết ngày đi/nghỉ sau khi tính lại HP (chỉ hiện khi 1 học sinh) */}
                      {recalcResult && selectedTuitionDetails.length === 1 && (
                        <div style={{
                          background: '#fef9ec', border: '1.5px solid #fbbf24', borderRadius: 8,
                          padding: '10px 12px', marginBottom: 6, fontSize: '0.82rem', lineHeight: 1.7,
                        }}>
                          <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                            📊 Chi tiết điềm danh T{detail.month}/{detail.year} (tới trước ngày {new Date(quitDateForRecalc + 'T00:00:00').toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}):
                          </div>
                          {recalcResult.attendedDates.length > 0 ? (
                            <div style={{ color: '#065f46' }}>
                              <span style={{ fontWeight: 600 }}>✅ Đã đi ({recalcResult.attendedCount} buổi):</span>{' '}
                              {recalcResult.attendedDates.join('; ')}
                            </div>
                          ) : (
                            <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>✅ Chưa có buổi đi nào được ghi nhận.</div>
                          )}
                          {recalcResult.absentDates.length > 0 && (
                            <div style={{ color: '#b91c1c', marginTop: 2 }}>
                              <span style={{ fontWeight: 600 }}>❌ Nghỉ tháng này ({recalcResult.absentCountThisMonth} buổi):</span>{' '}
                              {recalcResult.absentDates.join('; ')}
                            </div>
                          )}
                          {recalcResult.absencesLastMonth > 0 && (
                            <div style={{ color: '#d97706', marginTop: 2 }}>
                              <span style={{ fontWeight: 600 }}>➖ Nghỉ T{previousMonth} ({recalcResult.absencesLastMonth} buổi):</span>{' '}
                              {formatCurrency(recalcResult.absencesLastMonth * recalcResult.feePerSession)} đ (khấu trừ)
                            </div>
                          )}
                          <div style={{ borderTop: '1px solid #fcd34d', marginTop: 6, paddingTop: 6, fontWeight: 700, color: '#92400e' }}>
                            💰 Tổng HP tính lại:{' '}
                            <span style={{ color: '#16a34a', fontSize: '1rem' }}>{formatCurrency(recalcResult.newTotalFee)} đ</span>
                            <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.78rem', marginLeft: 6 }}>
                              = {recalcResult.attendedCount} buổi × {formatCurrency(recalcResult.feePerSession)}{recalcResult.absencesLastMonth > 0 ? ` − ${recalcResult.absencesLastMonth} × ${formatCurrency(recalcResult.feePerSession)}` : ''}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Extra unpaid months */}
                  {loadingUnpaidMonths ? (
                    <div style={{ color: '#888', fontSize: '0.83rem', marginTop: '6px' }}>Đang tải tháng chưa nộp...</div>
                  ) : (
                    selectedTuitionDetails.map((detail: any) => {
                      const unpaidList = (unpaidMonthsMap[detail.studentId] || []);
                      if (unpaidList.length === 0) return null;
                      return (
                        <div key={`extras-${detail.studentId}`} style={{ marginTop: '6px' }}>
                          {selectedTuitionDetails.length > 1 && (
                            <div style={{ fontSize: '0.78rem', color: '#6366f1', fontWeight: 600, marginBottom: '4px', paddingLeft: '4px' }}>
                              Tháng nợ cũ — {detail.student?.name}:
                            </div>
                          )}
                          {selectedTuitionDetails.length === 1 && unpaidList.length > 0 && (
                            <div style={{ fontSize: '0.78rem', color: '#6366f1', fontWeight: 600, marginBottom: '4px', paddingLeft: '4px' }}>
                              Tháng nợ cũ chưa thu:
                            </div>
                          )}
                          {unpaidList.map((m: any) => {
                            const checked = (extraMonthsSelected[detail.studentId] || []).includes(m.tuitionDetailId);
                            return (
                              <div key={m.tuitionDetailId}
                                onClick={() => toggleExtraMonth(detail.studentId, m.tuitionDetailId)}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '6px 10px', borderRadius: '8px', cursor: 'pointer',
                                  background: checked ? '#fef3c7' : '#fff',
                                  border: `1.5px solid ${checked ? '#f59e0b' : '#e5e7eb'}`,
                                  marginBottom: '5px',
                                  transition: 'all 0.15s',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <input type="checkbox" checked={checked} readOnly
                                    style={{ width: '15px', height: '15px', accentColor: '#f59e0b' }} />
                                  <span style={{ fontWeight: 500, color: checked ? '#92400e' : '#374151' }}>
                                    T{m.month}/{m.year}
                                  </span>
                                </div>
                                <span style={{ fontWeight: 600, color: checked ? '#d97706' : '#6b7280' }}>
                                  {formatCurrency(m.totalFee)} đ
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  )}

                  {/* Grand total */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginTop: '10px', paddingTop: '10px', borderTop: '1.5px solid #c7d2fe',
                  }}>
                    <span style={{ fontWeight: 700, color: '#1e40af' }}>Tổng cộng:</span>
                    <span style={{ fontWeight: 800, fontSize: '1.15rem', color: '#16a34a' }}>
                      {formatCurrency(computeInvoiceTotal())} đ
                    </span>
                  </div>
                </div>

                <FormGroup>
                  <Label>Số tiền thanh toán</Label>
                  <Input
                    type="number"
                    value={computeInvoiceTotal()}
                    onChange={(e) =>
                      setInvoiceFormData({ ...invoiceFormData, amount: parseFloat(e.target.value) })
                    }
                  />
                  <small style={{ color: '#6c757d' }}>Tự động cập nhật theo tháng đã chọn ở trên</small>
                </FormGroup>

                <FormGroup>
                  <Label>Ngày thanh toán</Label>
                  <Input
                    type="date"
                    value={invoiceFormData.paymentDate}
                    onChange={(e) =>
                      setInvoiceFormData({ ...invoiceFormData, paymentDate: e.target.value })
                    }
                  />
                </FormGroup>

                <FormGroup>
                  <Label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Hình thức thanh toán <span style={{ color: 'red' }}>*</span>
                  </Label>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {[{ value: 'cash', label: '💵 Tiền mặt' }, { value: 'transfer', label: '🏦 Chuyển khoản' }].map(opt => (
                      <label
                        key={opt.value}
                        onClick={() => setInvoiceFormData({ ...invoiceFormData, paymentMethod: opt.value })}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '8px 18px', borderRadius: '8px', cursor: 'pointer',
                          border: `2px solid ${invoiceFormData.paymentMethod === opt.value ? '#0d6efd' : '#dee2e6'}`,
                          background: invoiceFormData.paymentMethod === opt.value ? '#e7f1ff' : '#fff',
                          fontWeight: invoiceFormData.paymentMethod === opt.value ? 600 : 400,
                          color: invoiceFormData.paymentMethod === opt.value ? '#0d6efd' : '#495057',
                          transition: 'all 0.15s',
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={opt.value}
                          checked={invoiceFormData.paymentMethod === opt.value}
                          onChange={() => setInvoiceFormData({ ...invoiceFormData, paymentMethod: opt.value })}
                          style={{ accentColor: '#0d6efd' }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                  {!invoiceFormData.paymentMethod && (
                    <small style={{ color: '#dc3545', marginTop: '4px', display: 'block' }}>Bắt buộc chọn hình thức thanh toán</small>
                  )}
                </FormGroup>

                {/* Image upload for transfer payment */}
                {invoiceFormData.paymentMethod === 'transfer' && (
                  <FormGroup>
                    <Label>Ảnh chuyển khoản</Label>
                    <div
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      style={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#f9f9f9',
                        cursor: 'pointer',
                        minHeight: '150px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      onClick={() => document.getElementById('transferImageInput')?.click()}
                    >
                      {transferImagePreview ? (
                        <div>
                          <img
                            src={transferImagePreview}
                            alt="Preview"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '200px',
                              borderRadius: '4px',
                              marginBottom: '10px',
                            }}
                          />
                          <p style={{ marginTop: '10px', color: '#28a745', fontWeight: 'bold' }}>
                            ✓ Đã chọn ảnh
                          </p>
                          <Button
                            size="sm"
                            color="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTransferImage(null);
                              setTransferImagePreview('');
                            }}
                          >
                            Xóa ảnh
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <p style={{ fontSize: '3rem', margin: '0' }}>📷</p>
                          <p style={{ margin: '10px 0', fontWeight: 'bold' }}>
                            Kéo thả ảnh vào đây hoặc click để chọn
                          </p>
                          <p style={{ margin: '0', fontSize: '0.9rem', color: '#666' }}>
                            Hỗ trợ: JPG, PNG (Tối đa 10MB)
                          </p>
                        </div>
                      )}
                    </div>
                    <Input
                      id="transferImageInput"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                  </FormGroup>
                )}

                <FormGroup>
                  <Label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Người nộp học phí <span style={{ color: 'red' }}>*</span>
                  </Label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                    {(() => {
                      // Tên thực của học sinh trong hoá đơn (chỉ dùng khi tiền mặt)
                      const studentNames = selectedTuitionDetails.map((d: any) => d.student?.name).filter(Boolean).join(', ');
                      const selfPayerOpt = invoiceFormData.paymentMethod === 'cash' && studentNames
                        ? { label: studentNames, value: studentNames, isSelf: true }
                        : null;

                      const fixedOpts = ['Bố', 'Mẹ', 'Bà', 'Ông', 'Khác'];
                      const knownValues = ['Bố', 'Mẹ', 'Bà', 'Ông', studentNames, ''].filter(Boolean);

                      const allOpts = [
                        ...['Bố', 'Mẹ', 'Bà', 'Ông'].map(v => ({ label: v, value: v, isSelf: false })),
                        ...(selfPayerOpt ? [selfPayerOpt] : []),
                        { label: 'Khác', value: 'Khác', isSelf: false },
                      ];

                      return allOpts.map(opt => {
                        const isOther = opt.value === 'Khác';
                        const isSelected = isOther
                          ? !knownValues.includes(invoiceFormData.payerName) && invoiceFormData.payerName !== ''
                          : invoiceFormData.payerName === opt.value;
                        return (
                          <label
                            key={opt.label}
                            onClick={() => {
                              if (!isOther) setInvoiceFormData({ ...invoiceFormData, payerName: opt.value });
                              else if (isSelected) { /* keep text */ }
                              else setInvoiceFormData({ ...invoiceFormData, payerName: '__other__' });
                            }}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                              padding: '6px 14px', borderRadius: '20px', cursor: 'pointer',
                              border: `2px solid ${isSelected ? (opt.isSelf ? '#0d6efd' : '#6f42c1') : '#dee2e6'}`,
                              background: isSelected ? (opt.isSelf ? '#e7f1ff' : '#f3e8ff') : '#fff',
                              fontWeight: isSelected ? 600 : 400,
                              color: isSelected ? (opt.isSelf ? '#0d6efd' : '#6f42c1') : '#495057',
                              transition: 'all 0.15s',
                              userSelect: 'none',
                            }}
                          >
                            <input
                              type="radio"
                              name="payerName"
                              checked={isSelected}
                              readOnly
                              style={{ accentColor: opt.isSelf ? '#0d6efd' : '#6f42c1' }}
                            />
                            {opt.label}
                          </label>
                        );
                      });
                    })()}
                  </div>
                  {/* Text input when 'Khác' is selected */}
                  {(() => {
                    const studentNames = selectedTuitionDetails.map((d: any) => d.student?.name).filter(Boolean).join(', ');
                    const knownValues = ['Bố', 'Mẹ', 'Bà', 'Ông', studentNames, '', '__other__'].filter(Boolean);
                    const showInput = !knownValues.includes(invoiceFormData.payerName) || invoiceFormData.payerName === '__other__';
                    return showInput ? (
                      <Input
                        type="text"
                        value={invoiceFormData.payerName === '__other__' ? '' : invoiceFormData.payerName}
                        onChange={(e) => setInvoiceFormData({ ...invoiceFormData, payerName: e.target.value || '__other__' })}
                        placeholder="Nhập tên người nộp..."
                        autoFocus
                        style={{ marginTop: '4px' }}
                      />
                    ) : null;
                  })()}
                  {(!invoiceFormData.payerName || invoiceFormData.payerName === '__other__') && (
                    <small style={{ color: '#dc3545', marginTop: '4px', display: 'block' }}>Bắt buộc nhập tên người nộp</small>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Ghi chú</Label>
                  <Input
                    type="textarea"
                    value={invoiceFormData.notes}
                    onChange={(e) =>
                      setInvoiceFormData({ ...invoiceFormData, notes: e.target.value })
                    }
                    placeholder="Ghi chú thêm (không bắt buộc)"
                  />
                </FormGroup>
              </>
            )}
          </ModalBody>
          <ModalFooter style={{ gap: 8 }}>
            <Button color="secondary" onClick={toggleInvoiceModal}>Hủy</Button>

            {/* Nút Copy xác nhận — chỉ hiện khi thanh toán tiền mặt */}
            {invoiceFormData.paymentMethod === 'cash' && selectedTuitionDetails.length > 0 && (() => {
              const allMonths: { month: number; year: number }[] = [];
              selectedTuitionDetails.forEach((detail: any) => {
                allMonths.push({ month: detail.month, year: detail.year });
                (extraMonthsSelected[detail.studentId] || []).forEach((detailId: number) => {
                  const m = (unpaidMonthsMap[detail.studentId] || []).find((u: any) => u.tuitionDetailId === detailId);
                  if (m) allMonths.push({ month: m.month, year: m.year });
                });
              });
              const uniqueMonths = allMonths
                .filter((m, i, arr) => arr.findIndex(x => x.month === m.month && x.year === m.year) === i)
                .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
              const years = [...new Set(uniqueMonths.map(m => m.year))];
              const monthNums = uniqueMonths.map(m => m.month);
              const monthStr = monthNums.join(', ');
              const yearStr = years.join('/');
              const studentNames = selectedTuitionDetails.map((d: any) => d.student?.name).filter(Boolean).join(', ');
              const total = computeInvoiceTotal().toLocaleString('vi-VN');
              const payDate = invoiceFormData.paymentDate
                ? (() => { const d = new Date(invoiceFormData.paymentDate); return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`; })()
                : '';
              const payerLabel = invoiceFormData.payerName || '___';
              const titleLine = `🙇 [Thông báo đã hoàn thành học phí tháng ${monthStr}/${yearStr}]:`;
              const text = `${titleLine}\n- Học sinh: ${studentNames}\n- Đã hoàn thành học phí tháng: ${monthStr}\n- Tổng: ${total}\n- Người nộp trực tiếp: ${payerLabel} (Ngày ${payDate})`;
              return (
                <Button
                  color={hasCopiedConfirm ? 'success' : 'info'}
                  onClick={() => {
                    navigator.clipboard.writeText(text).then(() => {
                      setHasCopiedConfirm(true);
                    });
                  }}
                >
                  {hasCopiedConfirm ? '✅ Đã copy xác nhận' : '📋 Copy xác nhận T' + monthStr}
                </Button>
              );
            })()}

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              {invoiceFormData.paymentMethod === 'cash' && !hasCopiedConfirm && (
                <span style={{ fontSize: '0.75rem', color: '#dc3545' }}>
                  ⚠️ Vui lòng Copy xác nhận trước khi lưu
                </span>
              )}
              <Button
                color="primary"
                onClick={handleSaveInvoice}
                disabled={invoiceFormData.paymentMethod === 'cash' && !hasCopiedConfirm}
              >
                {invoiceFormData.paymentMethod === 'cash' && !hasCopiedConfirm ? '🔒 Lưu hóa đơn' : '💾 Lưu hóa đơn'}
              </Button>
            </div>
          </ModalFooter>
        </Modal>

        {/* Invoice Detail Modal */}
        <Modal isOpen={invoiceDetailModal} toggle={() => setInvoiceDetailModal(false)} size="lg">
          <ModalHeader toggle={() => setInvoiceDetailModal(false)}>
            Chi tiết hóa đơn
          </ModalHeader>
          <ModalBody>
            {selectedInvoiceForView && (
              <div>
                <Row style={{ marginBottom: '20px' }}>
                  <Col md={6}>
                    <p><strong>Số hóa đơn:</strong> {selectedInvoiceForView.invoiceNumber}</p>
                    <p><strong>Học sinh:</strong> {selectedInvoiceForView.student?.name}</p>
                    <p><strong>Tháng:</strong> {selectedInvoiceForView.tuitionDetail?.month}/{selectedInvoiceForView.tuitionDetail?.year}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Số tiền:</strong> <span style={{ color: '#28a745', fontSize: '1.2rem', fontWeight: 'bold' }}>{formatCurrency(parseFloat(selectedInvoiceForView.amount))} đ</span></p>
                    <p><strong>Ngày thanh toán:</strong> {new Date(selectedInvoiceForView.paymentDate).toLocaleDateString('vi-VN')}</p>
                    <p><strong>Hình thức:</strong> {selectedInvoiceForView.paymentMethod === 'cash' ? 'Tiền mặt' : 'Chuyển khoản'}</p>
                  </Col>
                </Row>

                {selectedInvoiceForView.payerName && (
                  <p><strong>Người thanh toán:</strong> {selectedInvoiceForView.payerName}</p>
                )}

                {selectedInvoiceForView.notes && (
                  <p><strong>Ghi chú:</strong> {selectedInvoiceForView.notes}</p>
                )}

                {selectedInvoiceForView.transferImageUrl && (
                  <div style={{ marginTop: '15px' }}>
                    <strong>Ảnh chuyển khoản:</strong>
                    <div style={{ marginTop: '10px' }}>
                      <img
                        src={selectedInvoiceForView.transferImageUrl}
                        alt="Transfer receipt"
                        style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="success" onClick={() => selectedInvoiceForView && handleDownloadPDF(selectedInvoiceForView.id)}>
              📄 Tải PDF
            </Button>
            <Button color="secondary" onClick={() => setInvoiceDetailModal(false)}>
              Đóng
            </Button>
          </ModalFooter>
        </Modal>
        {/* ——— Modal Sửa học phí ——— */}
        <Modal isOpen={editDetailModal} toggle={() => setEditDetailModal(false)} centered size="md">
          <ModalHeader toggle={() => setEditDetailModal(false)}>
            ✏️ Sửa học phí — {editingDetail?.student?.name} (T{selectedMonth}/{selectedYear})
          </ModalHeader>
          <ModalBody style={{ fontSize: '0.92rem' }}>
            {editingDetail && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Số buổi tháng này</label>
                    <Input
                      type="number" min={0}
                      value={editDetailForm.sessionsThisMonth}
                      onChange={e => {
                        const v = Number(e.target.value);
                        const newForm = {
                          ...editDetailForm, sessionsThisMonth: v,
                          currentMonthFee: v * editDetailForm.feePerSession
                        };
                        setEditDetailForm({ ...newForm, totalFee: recalcEditDetailTotal(newForm) });
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Học phí/buổi (đ)</label>
                    <Input
                      type="number" min={0}
                      value={editDetailForm.feePerSession}
                      onChange={e => {
                        const v = Number(e.target.value);
                        const newForm = {
                          ...editDetailForm, feePerSession: v,
                          currentMonthFee: editDetailForm.sessionsThisMonth * v,
                          lastMonthAbsenceFee: editDetailForm.absencesLastMonth * v
                        };
                        setEditDetailForm({ ...newForm, totalFee: recalcEditDetailTotal(newForm) });
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Buổi nghỉ T{selectedMonth - 1 || 12} (trước)</label>
                    <Input
                      type="number" min={0}
                      value={editDetailForm.absencesLastMonth}
                      onChange={e => {
                        const v = Number(e.target.value);
                        const newForm = {
                          ...editDetailForm, absencesLastMonth: v,
                          lastMonthAbsenceFee: v * editDetailForm.feePerSession
                        };
                        setEditDetailForm({ ...newForm, totalFee: recalcEditDetailTotal(newForm) });
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>HP tháng này (tự tính)</label>
                    <Input
                      type="number" readOnly
                      value={editDetailForm.currentMonthFee}
                      style={{ background: '#f5f5f5', color: '#555' }}
                    />
                  </div>
                </div>

                {/* Phụ phí */}
                <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '12px 14px', border: '1px solid #dee2e6' }}>
                  <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={editDetailForm.hasExtraFee}
                      onChange={e => {
                        const newForm = { ...editDetailForm, hasExtraFee: e.target.checked };
                        setEditDetailForm({ ...newForm, totalFee: recalcEditDetailTotal(newForm) });
                      }}
                    />
                    Có phụ phí
                  </label>
                  {editDetailForm.hasExtraFee && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 3 }}>Số tiền phụ phí (đ)</label>
                        <Input
                          type="number" min={0}
                          value={editDetailForm.extraFeeAmount}
                          onChange={e => {
                            const newForm = { ...editDetailForm, extraFeeAmount: Number(e.target.value) };
                            setEditDetailForm({ ...newForm, totalFee: recalcEditDetailTotal(newForm) });
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.85rem', display: 'block', marginBottom: 3 }}>Ghi chú phụ phí</label>
                        <Input
                          type="text"
                          placeholder="vd: Học bù, vật liệu..."
                          value={editDetailForm.extraFeeLabel}
                          onChange={e => setEditDetailForm({ ...editDetailForm, extraFeeLabel: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Tổng cộng */}
                <div style={{
                  background: 'linear-gradient(135deg,#1e3a5f,#2563eb)',
                  borderRadius: 10, padding: '12px 18px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span style={{ color: '#93c5fd', fontWeight: 600 }}>Tổng học phí</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Input
                      type="number" min={0}
                      value={editDetailForm.totalFee}
                      onChange={e => setEditDetailForm({ ...editDetailForm, totalFee: Number(e.target.value) })}
                      style={{
                        width: 130, fontWeight: 700, fontSize: '1rem',
                        textAlign: 'right', border: '2px solid #60a5fa',
                        borderRadius: 6, background: '#1e40af', color: '#fff',
                      }}
                    />
                    <span style={{ color: '#93c5fd' }}>đ</span>
                  </div>
                </div>

                <div style={{ fontSize: '0.8rem', color: '#888', fontStyle: 'italic' }}>
                  💡 Tổng = (Số buổi × Học phí/buổi) − (Buổi nghỉ T.trước × Học phí/buổi) + Phụ phí.&nbsp;
                  Bạn có thể ghi đè tổng thủ công.
                </div>

                {/* Ghi chú lý do chỉnh sửa */}
                <div>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    📝 Lý do chỉnh sửa HP <span style={{ fontWeight: 400, fontSize: '0.82rem', color: '#888' }}>(bắt buộc nếu thay đổi)</span>
                  </label>
                  <Input
                    type="textarea"
                    rows={2}
                    placeholder="vd: Học sinh nghỉ bệnh có phép nên bớt 1 buổi, Thỏa thuận riêng với phụ huynh..."
                    value={editDetailForm.note}
                    onChange={e => setEditDetailForm({ ...editDetailForm, note: e.target.value })}
                    style={{ fontSize: '0.88rem', resize: 'vertical' }}
                  />
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleSaveTuitionDetail}
              disabled={isSavingDetail}
            >
              {isSavingDetail ? '⏳ Đang lưu...' : '💾 Lưu học phí'}
            </Button>
            <Button color="secondary" onClick={() => setEditDetailModal(false)}>Hủy</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </PageWrapper>
  );
};

export default TuitionStatistics;
